import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import { API_BASE_URL } from './config';
import JsonEditor from './components/JsonEditor';
import SearchableSelect from './components/SearchableSelect';
import ExpressionInput from './components/ExpressionInput';
import FormBuilder from './components/FormBuilder';
import ConditionBuilder from './components/ConditionBuilder';
import CodeEditor from './components/CodeEditor';
import ResourcePicker from './components/ResourcePicker';
import ModelCheatSheet from './components/ModelCheatSheet';
import SchemaBrowser from './components/SchemaBrowser';
import useOAuthConnect from './hooks/useOAuthConnect';

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontWeight: 500,
  color: 'var(--muted)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const helpStyle = {
  display: 'block',
  marginTop: '-2px',
  marginBottom: '8px',
  color: 'var(--dim)',
  fontSize: '11px',
  lineHeight: '1.5',
};

const requiredMarkStyle = {
  color: 'var(--error)',
  marginLeft: '3px',
  fontWeight: 500,
};

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  marginBottom: '10px',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  fontSize: '13px',
  background: 'var(--bg)',
  color: 'var(--text)',
  outline: 'none',
  transition: 'border-color 120ms ease',
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  marginBottom: '8px',
  userSelect: 'none',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--text-soft)',
};

const sectionBodyStyle = {
  background: 'var(--panel-2)',
  padding: '12px',
  borderRadius: '6px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  border: '1px solid var(--border)',
};

const textareaStyle = {
  ...inputStyle,
  minHeight: '80px',
  resize: 'vertical'
};

const containerStyle = {
  background: 'var(--panel)',
  borderLeft: '1px solid var(--border)',
  padding: '14px 16px',
  zIndex: 10,
  overflowY: 'auto',
  color: 'var(--text)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  height: '100%',
  boxSizing: 'border-box',
};

const resolvePath = (path, context) => {
  if (path === 'now') {
      return new Date().toISOString();
  }
  // Handle basic strftime-like formatting in frontend preview (simple approximation)
  // e.g. now.strftime('%Y-%m-%d')
  if (path.startsWith('now.strftime')) {
      try {
          const match = path.match(/strftime\(['"](.*)['"]\)/);
          if (match) {
              const format = match[1];
              const now = new Date();
              // Very basic replacement for common Python strftime directives
              let result = format
                  .replace('%Y', now.getFullYear())
                  .replace('%m', String(now.getMonth() + 1).padStart(2, '0'))
                  .replace('%d', String(now.getDate()).padStart(2, '0'))
                  .replace('%H', String(now.getHours()).padStart(2, '0'))
                  .replace('%M', String(now.getMinutes()).padStart(2, '0'))
                  .replace('%S', String(now.getSeconds()).padStart(2, '0'));
              return result;
          }
      } catch (e) {
          return new Date().toISOString();
      }
  }

  // Try to evaluate as a JavaScript expression (for JSON.stringify and complex logic)
  try {
      // Use new Function to evaluate safely with context
      // We expose 'nodes' and 'env' from context
      // We check if path contains complex characters that imply an expression
      if (path.includes('(') || path.includes('{') || path.includes('JSON.')) {
           const func = new Function('nodes', 'env', 'JSON', `return ${path}`);
           return func(context.nodes || {}, context.env || {}, JSON);
      }
  } catch (e) {
      // Fall through to manual parsing if eval fails
  }

  try {
      let current = context;
      // Regex to match property access: .prop or ['prop'] or ["prop"]
      // We start with the full path, e.g. "nodes['Start Node'].data"
      
      const parts = [];
      let buffer = '';
      let inBracket = false;
      let quoteChar = null;
      
      for (let i = 0; i < path.length; i++) {
          const char = path[i];
          if (inBracket) {
              if (char === quoteChar) {
                  inBracket = false;
                  quoteChar = null;
                  parts.push(buffer);
                  buffer = '';
                  // Skip closing bracket
                   while(i+1 < path.length && (path[i+1] === ']' || path[i+1] === '.')) i++;
              } else {
                  buffer += char;
              }
          } else {
              if (char === '.') {
                  if (buffer) parts.push(buffer);
                  buffer = '';
              } else if (char === '[') {
                  if (buffer) parts.push(buffer);
                  buffer = '';
                  if (i+1 < path.length && (path[i+1] === "'" || path[i+1] === '"')) {
                      quoteChar = path[i+1];
                      inBracket = true;
                      i++;
                  } else {
                      // Unquoted bracket (numeric index): read until ']'
                      let numBuf = '';
                      i++;
                      while (i < path.length && path[i] !== ']') { numBuf += path[i]; i++; }
                      if (numBuf) parts.push(numBuf);
                      // Skip optional trailing dot
                      if (i + 1 < path.length && path[i+1] === '.') i++;
                  }
              } else {
                  buffer += char;
              }
          }
      }
      if (buffer) parts.push(buffer);

      for (const part of parts) {
          if (current === undefined || current === null) return undefined;
          current = current[part];
      }
      return current;
  } catch (e) {
      return undefined;
  }
};

const VariablePreview = ({ value, context }) => {
  if (!value || typeof value !== 'string' || !value.includes('{{')) return null;

  try {
      // Check if it's a single variable expression ([\s\S]*? matches across newlines)
      const fullMatch = value.match(/^\s*\{\{\s*([\s\S]*?)\s*\}\}\s*$/);
      if (fullMatch) {
          const val = resolvePath(fullMatch[1].trim(), context);
          if (val !== undefined) {
               const display = typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val);
               return (
                  <div style={{ 
                      fontSize: '11px', 
                      color: 'var(--accent-color)', 
                      marginTop: '4px', 
                      padding: '4px', 
                      borderLeft: '2px solid var(--accent-color)',
                      background: 'var(--bg-tertiary)',
                      wordBreak: 'break-all',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap'
                  }}>
                      Preview: {display}
                  </div>
              );
          }
      }

      // Mixed content ([\s\S]*? matches across newlines)
      let hasMatch = false;
      const resolvedString = value.replace(/\{\{\s*([\s\S]*?)\s*\}\}/g, (match, path) => {
          const val = resolvePath(path.trim(), context);
          if (val === undefined) return match;
          hasMatch = true;
          return typeof val === 'object' ? JSON.stringify(val) : String(val);
      });

      if (!hasMatch) return null;

      return (
          <div style={{ 
              fontSize: '11px', 
              color: 'var(--accent-color)', 
              marginTop: '4px', 
              padding: '4px', 
              borderLeft: '2px solid var(--accent-color)',
              background: 'var(--bg-tertiary)',
              wordBreak: 'break-all',
              maxHeight: '100px',
              overflowY: 'auto'
          }}>
              Preview: {resolvedString}
          </div>
      );
  } catch (e) {
      return null;
  }
};

const AttachmentSelector = ({ value, onChange, context }) => {
  // Try to determine mode from value
  const [mode, setMode] = useState('list'); // 'list' or 'variable'
  const [items, setItems] = useState([]);
  const [variableText, setVariableText] = useState('');

  // Initialize state from value prop
  useEffect(() => {
    if (!value) {
        setItems([]);
        setVariableText('');
        setMode('list');
        return;
    }

    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            setMode('list');
            setItems(parsed);
            // Sync variable text too just in case
            setVariableText(value); 
        } else {
            setMode('variable');
            setVariableText(value);
        }
    } catch (e) {
        // Not JSON, assume variable/string
        setMode('variable');
        setVariableText(value);
    }
  }, [value]);

  const updateValue = (newMode, newItems, newVarText) => {
      if (newMode === 'list') {
          onChange(JSON.stringify(newItems));
      } else {
          onChange(newVarText);
      }
  };

  const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Clear input immediately to allow re-selection
      e.target.value = '';

      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      try {
          const res = await fetch(`${API_BASE_URL}/files/upload`, {
              method: 'POST',
              body: formData,
              headers: headers
          });
          if (!res.ok) throw new Error("Upload failed");
          const data = await res.json();
          // Store just the path string as expected by backend nodes
          // OR if path is null, store the data object which backend nodes should handle
          const newItem = data.path || data.data;
          const newItems = [...items, newItem];
          setItems(newItems);
          updateValue('list', newItems, variableText);
      } catch (err) {
          console.error("Upload failed", err);
          alert("Upload failed: " + err.message);
      }
  };

  const removeFile = (index) => {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      updateValue('list', newItems, variableText);
  };

  return (
      <div style={{ marginBottom: '15px', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '10px', background: 'var(--bg-tertiary)' }}>
          <label style={{...labelStyle, marginBottom: '10px'}}>Attachments</label>
          <div style={{ display: 'flex', marginBottom: '10px', gap: '10px' }}>
              <button 
                onClick={() => { setMode('list'); updateValue('list', items, variableText); }}
                style={{
                    flex: 1,
                    padding: '6px',
                    background: mode === 'list' ? 'var(--accent-color)' : 'transparent',
                    color: mode === 'list' ? '#fff' : 'var(--text-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}
              >
                Upload Files
              </button>
              <button 
                onClick={() => { setMode('variable'); updateValue('variable', items, variableText); }}
                style={{
                    flex: 1,
                    padding: '6px',
                    background: mode === 'variable' ? 'var(--accent-color)' : 'transparent',
                    color: mode === 'variable' ? '#fff' : 'var(--text-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}
              >
                Use Variable
              </button>
          </div>

          {mode === 'list' && (
              <div>
                  <div style={{ marginBottom: '10px' }}>
                      {items.map((item, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px', fontSize: '12px', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '4px', border: '1px solid var(--border-color)' }}>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-primary)' }}>
                                  {typeof item === 'string' ? item.split(/[/\\]/).pop() : JSON.stringify(item)}
                              </span>
                              <button 
                                onClick={() => removeFile(idx)}
                                style={{ background: 'transparent', border: 'none', color: 'var(--error-color, #ff4d4f)', cursor: 'pointer', fontWeight: 'bold' }}
                              >
                                ✕
                              </button>
                          </div>
                      ))}
                      {items.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontStyle: 'italic', textAlign: 'center', padding: '10px' }}>No files selected</div>}
                  </div>
                  <label style={{ 
                      display: 'block', 
                      padding: '8px', 
                      background: 'var(--bg-secondary)', 
                      border: '1px dashed var(--border-color)', 
                      borderRadius: '4px', 
                      cursor: 'pointer', 
                      fontSize: '12px',
                      width: '100%',
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      color: 'var(--text-secondary)'
                  }}>
                      + Click to Upload File
                      <input type="file" onChange={handleFileUpload} style={{ display: 'none' }} />
                  </label>
              </div>
          )}

          {mode === 'variable' && (
              <div>
                  <textarea
                    style={{ ...textareaStyle, marginBottom: 0 }}
                    value={variableText}
                    onChange={(e) => {
                        setVariableText(e.target.value);
                        updateValue('variable', items, e.target.value);
                    }}
                    placeholder='{{chat_trigger_1.files}} or ["/path/to/file"]'
                  />
                  <VariablePreview value={variableText} context={context} />
              </div>
          )}
      </div>
  );
};

let WEBHOOK_BASE_URL = import.meta.env.VITE_WEBHOOK_BASE_URL ?? API_BASE_URL;

if (!WEBHOOK_BASE_URL || WEBHOOK_BASE_URL.startsWith('/')) {
   const origin = window.location.origin;
   WEBHOOK_BASE_URL = `${origin}${WEBHOOK_BASE_URL}`;
}

function XavvyFormPicker({ value, onChange, workspaceId }) {
  const [forms, setForms] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [showNewInput, setShowNewInput] = React.useState(false);
  const [newName, setNewName] = React.useState('');

  const load = React.useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const qs = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    fetch(`${API_BASE_URL}/xavvy-forms${qs}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.json())
      .then(data => { setForms(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workspaceId]);

  React.useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const trimmed = String(newName).trim();
    if (!trimmed) return;
    setSaving(true);
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    const defaultDef = {
      settings: { title: trimmed, description: '', submitLabel: 'Submit', successMessage: 'Thank you!', redirectUrl: '', honeypot: true, multiStep: false, autoSave: true, customCss: '' },
      theme: { primaryColor: '#6366f1', backgroundColor: '#0f172a', cardBackground: '#1e293b', textColor: '#f1f5f9', borderRadius: '10px', fontFamily: 'Inter, sans-serif', layout: 'card' },
      elements: [],
    };
    try {
      const res = await fetch(`${API_BASE_URL}/xavvy-forms`, { method: 'POST', headers, body: JSON.stringify({ name: trimmed, definition: defaultDef, is_case_manager_form: true, ...(workspaceId ? { workspace_id: workspaceId } : {}) }) });
      const form = await res.json();
      onChange(form.id);
      setShowNewInput(false);
      setNewName('');
      load();
      window.dispatchEvent(new CustomEvent('xavvy:open-form-designer', { detail: { formId: form.id } }));
    } catch (e) {
      alert('Failed to create form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, flex: 1 }}
          disabled={loading}
        >
          <option value="">{loading ? 'Loading forms...' : '— Select a form —'}</option>
          {forms.filter(f => f.is_case_manager_form).map(f => (
            <option key={f.id} value={f.id}>{f.name}{f.status === 'draft' ? ' (draft)' : ''}</option>
          ))}
        </select>
        <button
          onClick={load}
          title="Refresh form list"
          style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border-color, #334155)', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 13, flexShrink: 0 }}
        >⟳</button>
        {value && (
          <button
            title="Open in xavvy Forms designer"
            onClick={() => window.dispatchEvent(new CustomEvent('xavvy:open-form-designer', { detail: { formId: value } }))}
            style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border-color, #334155)', background: 'none', color: 'var(--accent-color, #6366f1)', cursor: 'pointer', fontSize: 13, flexShrink: 0 }}
          >✏️</button>
        )}
      </div>
      {showNewInput ? (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <input
            autoFocus
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); else if (e.key === 'Escape') { setShowNewInput(false); setNewName(''); } }}
            placeholder="Form name…"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            onClick={handleCreate}
            disabled={saving || !newName.trim()}
            style={{ padding: '5px 10px', borderRadius: 5, border: 'none', background: 'var(--accent-color, #6366f1)', color: '#fff', cursor: 'pointer', fontSize: 12, flexShrink: 0, opacity: saving || !newName.trim() ? 0.5 : 1 }}
          >{saving ? '…' : 'Create'}</button>
          <button
            onClick={() => { setShowNewInput(false); setNewName(''); }}
            style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border-color, #334155)', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
          >✕</button>
        </div>
      ) : (
        <button
          onClick={() => setShowNewInput(true)}
          style={{ padding: '5px 10px', borderRadius: 5, border: '1px dashed var(--border-color, #334155)', background: 'none', color: 'var(--accent-color, #6366f1)', cursor: 'pointer', fontSize: 12, textAlign: 'left' }}
        >+ New Form…</button>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
        Only forms marked "Use in xavvy Case Manager" appear here. Toggle the flag in the form's Settings panel to enable it for case work.
      </div>
    </div>
  );
}

function KeyValueEditor({
  value,
  onChange,
  keyLabel = 'Key',
  valueLabel = 'Value',
  keyOptions = null,          // if set, key side renders as <select>
  valueInput = 'text',        // 'text' | 'expression'
  expressionProps = null,     // { ancestorNodes, nodeStatus, envVars }
}) {
  const parsePairs = (raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return Object.entries(raw).map(([k, v]) => [k, typeof v === 'string' ? v : JSON.stringify(v)]);
    }
    if (Array.isArray(raw)) {
      return raw.map(p => [p.key || '', p.value || '']);
    }
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return Object.entries(parsed).map(([k, v]) => [k, typeof v === 'string' ? v : JSON.stringify(v)]);
        }
      } catch (_e) { /* fall through */ }
    }
    return [];
  };

  const [pairs, setPairs] = React.useState(() => parsePairs(value));

  React.useEffect(() => {
    const parsed = parsePairs(value);
    const currentObj = {};
    pairs.forEach(([k, v]) => { if (k.trim()) currentObj[k.trim()] = v; });
    const incomingObj = {};
    parsed.forEach(([k, v]) => { if (k.trim()) incomingObj[k.trim()] = v; });
    if (JSON.stringify(currentObj) !== JSON.stringify(incomingObj) && parsed.length > 0) {
      setPairs(parsed);
    }
  }, [value]);

  const commit = (newPairs) => {
    setPairs(newPairs);
    const obj = {};
    newPairs.forEach(([k, v]) => { if (k.trim()) obj[k.trim()] = v; });
    onChange(obj);
  };

  const useExpression = valueInput === 'expression' && expressionProps;
  // Expression value renderer needs more room than a plain input, so switch to
  // a vertical per-pair layout when it's active.
  const verticalRows = useExpression;

  const renderKeyInput = (k, v, idx) => {
    if (keyOptions && Array.isArray(keyOptions)) {
      const known = keyOptions.some(opt => (opt.value ?? opt.key) === k);
      return (
        <select
          style={{ ...inputStyle, marginBottom: 0, flex: 1, minWidth: 0 }}
          value={k}
          onChange={(e) => { const np = [...pairs]; np[idx] = [e.target.value, v]; commit(np); }}
        >
          <option value="">— pick {keyLabel.toLowerCase()} —</option>
          {keyOptions.map(opt => {
            const val = opt.value ?? opt.key;
            return <option key={val} value={val}>{opt.label || val}</option>;
          })}
          {k && !known && <option value={k}>{k} (custom)</option>}
        </select>
      );
    }
    return (
      <input
        style={{ ...inputStyle, marginBottom: 0, flex: 1, minWidth: 0 }}
        value={k}
        onChange={(e) => { const np = [...pairs]; np[idx] = [e.target.value, v]; commit(np); }}
        placeholder={keyLabel.toLowerCase()}
      />
    );
  };

  const renderValueInput = (k, v, idx) => {
    if (useExpression) {
      return (
        <ExpressionInput
          value={v}
          onChange={(val) => { const np = [...pairs]; np[idx] = [k, val]; commit(np); }}
          placeholder={valueLabel.toLowerCase()}
          ancestorNodes={expressionProps.ancestorNodes || []}
          nodeStatus={expressionProps.nodeStatus || {}}
          envVars={expressionProps.envVars || []}
          fieldLabel={valueLabel}
        />
      );
    }
    return (
      <input
        style={{ ...inputStyle, marginBottom: 0, flex: 1, minWidth: 0 }}
        value={v}
        onChange={(e) => { const np = [...pairs]; np[idx] = [k, e.target.value]; commit(np); }}
        placeholder={valueLabel.toLowerCase()}
      />
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: verticalRows ? '10px' : '6px' }}>
      {pairs.length > 0 && !verticalRows && (
        <div style={{ display: 'flex', gap: '6px', fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600, padding: '0 2px' }}>
          <span style={{ flex: 1 }}>{keyLabel}</span>
          <span style={{ flex: 1 }}>{valueLabel}</span>
          <span style={{ width: '30px' }}></span>
        </div>
      )}
      {pairs.map(([k, v], idx) => (
        verticalRows ? (
          <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '6px', border: '1px solid var(--border-color)', borderRadius: '4px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600, flexShrink: 0 }}>{keyLabel}</span>
              {renderKeyInput(k, v, idx)}
              <button
                title="Remove"
                style={{ padding: '2px 8px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}
                onClick={() => { const np = [...pairs]; np.splice(idx, 1); commit(np); }}
              >&times;</button>
            </div>
            <div>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '2px' }}>{valueLabel}</div>
              {renderValueInput(k, v, idx)}
            </div>
          </div>
        ) : (
          <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {renderKeyInput(k, v, idx)}
            <span style={{ color: 'var(--text-secondary)', fontSize: '14px', flexShrink: 0 }}>&rarr;</span>
            {renderValueInput(k, v, idx)}
            <button
              style={{ padding: '4px 8px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}
              onClick={() => { const np = [...pairs]; np.splice(idx, 1); commit(np); }}
            >&times;</button>
          </div>
        )
      ))}
      <button
        style={{ padding: '4px 10px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', alignSelf: 'flex-start' }}
        onClick={() => setPairs(prev => [...prev, ['', '']])}
      >+ Add Mapping</button>
    </div>
  );
}

export default function NodeConfigPanel({ selectedNode, nodes, edges, setNodes, setEdges, setSelectedNode, nodeStatus, runNode, isRunning, onCreateCredential, currentWorkspaceId, workflowType = 'standard', nodeCatalog = [] }) {
  const [formData, setFormData] = useState({});
  const [label, setLabel] = useState("");
  const [error, setError] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  const [selectedToolConnectionKey, setSelectedToolConnectionKey] = useState("");
  const { deleteElements } = useReactFlow();

  const [width, setWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [caseTypeOptions, setCaseTypeOptions] = useState([]);
  const [discretionaryTaskTypeOptions, setDiscretionaryTaskTypeOptions] = useState([]);
  const [fieldCatalog, setFieldCatalog] = useState(null);
  const [teamOptions, setTeamOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [nodeDefinitions, setNodeDefinitions] = useState({});
  const [envVars, setEnvVars] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const [columnOptions, setColumnOptions] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState({ tables: false, columns: false });
  const [metadataError, setMetadataError] = useState("");
  const [selectedBuilderTable, setSelectedBuilderTable] = useState("");
  // Local UI state for collapsible sections (does NOT leak into workflow params)
  const [simpleRetryOpen, setSimpleRetryOpen] = useState(false);
  const [advancedRetryOpen, setAdvancedRetryOpen] = useState(false);
  const [rateLimitOpen, setRateLimitOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  const [fieldSearch, setFieldSearch] = useState('');
  const panelRef = useRef(null);
  const selectedNodeType = selectedNode?.data?.type || selectedNode?.type || "";

  // OAuth inline connect hook — refreshes credentials and auto-selects new one after connect
  const refreshCredentials = useCallback(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/credentials/`, { headers })
      .then(res => res.json())
      .then(data => setCredentials(data))
      .catch(() => {});
  }, []);

  const oauthHook = useOAuthConnect({
    onSuccess: useCallback((credentialId) => {
      refreshCredentials();
      // Auto-select the newly created credential
      if (credentialId) {
        handleChange('credential_id', credentialId);
      }
    }, [refreshCredentials]),
  });

  // Reverse map: service_type → OAuth provider name
  const SERVICE_TYPE_OAUTH_MAP = {
    google_account: 'google', microsoft: 'microsoft', slack: 'slack',
    github: 'github', notion: 'notion', hubspot: 'hubspot', linear: 'linear',
  };

  const helperDbNodeCredentialFields = {
    postgres_query: { field: 'credential_id', serviceType: 'postgres' },
    mysql_query: { field: 'credential_id', serviceType: 'mysql' },
    sql_server_query: { field: 'credential_id', serviceType: 'sqlserver' },
  };
  const helperTableNodeTypes = new Set(['table', 'table_insert', 'table_update', 'table_upsert', 'table_get', 'table_delete', 'table_query']);
  const helperDbNodeTypes = new Set(Object.keys(helperDbNodeCredentialFields));
  const tableActionByLegacyType = {
    table_insert: 'insert',
    table_update: 'update',
    table_upsert: 'upsert',
    table_delete: 'delete',
    table_get: 'get',
    table_query: 'query',
  };
  const resolvedDefinitionNodeType = helperTableNodeTypes.has(selectedNodeType) ? 'table' : selectedNodeType;

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(`${API_BASE_URL}/credentials/`, { headers })
      .then(res => res.json())
      .then(data => setCredentials(data))
      .catch(err => console.error("Failed to fetch credentials", err));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/global-variables`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEnvVars(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const definition = nodeDefinitions[resolvedDefinitionNodeType];
    const hasWorkflowField = (definition?.fields || []).some(f => f.type === 'workflow');
    if (!hasWorkflowField) return;
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/workflows/`, { headers })
      .then(res => res.json())
      .then(data => setWorkflowOptions(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [resolvedDefinitionNodeType, nodeDefinitions]);

  // Load workspace field catalog when editing a node that has a form_builder field
  // or a key_value field with field_source / field_source_field metadata.
  // Cheap even when not needed since it's just a single GET with small payload.
  useEffect(() => {
    const definition = nodeDefinitions[resolvedDefinitionNodeType];
    const needsCatalog = (definition?.fields || []).some(f =>
      f.type === 'form_builder' ||
      (f.type === 'key_value' && (f.field_source || f.field_source_field))
    );
    if (!needsCatalog || !currentWorkspaceId) { setFieldCatalog(null); return; }
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/api/casematrix/workspaces/${currentWorkspaceId}/field-catalog`, { headers })
      .then(res => res.ok ? res.json() : null)
      .then(data => setFieldCatalog(data && (data.case_fields || data.task_fields) ? data : { case_fields: [], task_fields: [] }))
      .catch(() => setFieldCatalog({ case_fields: [], task_fields: [] }));
  }, [resolvedDefinitionNodeType, nodeDefinitions, currentWorkspaceId]);

  useEffect(() => {
    const definition = nodeDefinitions[resolvedDefinitionNodeType];
    const hasCaseTypePicker = (definition?.fields || []).some(f => f.type === 'case_type_picker');
    if (!hasCaseTypePicker || !currentWorkspaceId) return;
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/api/casematrix/workspaces/${currentWorkspaceId}/case-types?active_only=true`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setCaseTypeOptions(Array.isArray(data) ? data : []))
      .catch(() => setCaseTypeOptions([]));
  }, [resolvedDefinitionNodeType, nodeDefinitions, currentWorkspaceId]);

  useEffect(() => {
    const definition = nodeDefinitions[resolvedDefinitionNodeType];
    const hasCaseTaskTypePicker = (definition?.fields || []).some(f => f.type === 'case_task_type_picker');
    if (!hasCaseTaskTypePicker || !currentWorkspaceId) return;
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/api/casematrix/workspaces/${currentWorkspaceId}/discretionary-task-types`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setDiscretionaryTaskTypeOptions(Array.isArray(data) ? data : []))
      .catch(() => setDiscretionaryTaskTypeOptions([]));
  }, [resolvedDefinitionNodeType, nodeDefinitions, currentWorkspaceId]);

  useEffect(() => {
    const definition = nodeDefinitions[resolvedDefinitionNodeType];
    const hasTeamSelect = (definition?.fields || []).some(f => f.type === 'team_select');
    if (!hasTeamSelect || !currentWorkspaceId) return;
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/api/casematrix/workspaces/${currentWorkspaceId}/teams`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setTeamOptions(Array.isArray(data) ? data : []))
      .catch(() => setTeamOptions([]));
  }, [resolvedDefinitionNodeType, nodeDefinitions, currentWorkspaceId]);

  useEffect(() => {
    const definition = nodeDefinitions[resolvedDefinitionNodeType];
    const hasRoleSelect = (definition?.fields || []).some(f => f.type === 'role_select');
    if (!hasRoleSelect || !currentWorkspaceId) return;
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/api/casematrix/workspaces/${currentWorkspaceId}/case-roles?active_only=true`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setRoleOptions(Array.isArray(data) ? data : []))
      .catch(() => setRoleOptions([]));
  }, [resolvedDefinitionNodeType, nodeDefinitions, currentWorkspaceId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/workspaces/`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => setWorkspaceOptions(Array.isArray(data) ? data : []))
      .catch(() => setWorkspaceOptions([]));
  }, []);

  const startResizing = useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (mouseMoveEvent) => {
      if (isResizing && panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect();
        const newWidth = rect.right - mouseMoveEvent.clientX;
        if (newWidth > 200 && newWidth < 800) {
          setWidth(newWidth);
        }
      }
    },
    [isResizing]
  );

  useEffect(() => {
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [resize, stopResizing]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(`${API_BASE_URL}/nodes`, { headers })
      .then(res => {
        if (!res.ok) {
           throw new Error(`Failed to fetch nodes: ${res.status}`);
        }
        return res.json();
      })
      .then(nodes => {
        if (!Array.isArray(nodes)) {
             console.error("Expected array of nodes, got:", nodes);
             return;
        }
        const defs = {};
        nodes.forEach(node => {
          defs[node.type] = node;
        });
        setNodeDefinitions(defs);
      })
      .catch(err => console.error("Failed to fetch node definitions:", err));
  }, []);

  useEffect(() => {
    if (selectedNode) {
      const savedParams = selectedNode.data.params || {};
      const def = nodeDefinitions[resolvedDefinitionNodeType];
      const withDefaults = { ...savedParams };
      if (def && Array.isArray(def.fields)) {
        def.fields.forEach(f => {
          if (f.default !== undefined && !(f.name in withDefaults)) {
            withDefaults[f.name] = f.default;
          }
        });
      }
      if ((resolvedDefinitionNodeType === 'table' || resolvedDefinitionNodeType === 'table_trigger')
        && String(withDefaults.scope || 'workspace') === 'workspace'
        && !withDefaults.workspace_id
        && currentWorkspaceId) {
        withDefaults.workspace_id = currentWorkspaceId;
      }
      setFormData(withDefaults);
      setLabel(selectedNode.data.label || "");
      setError(null);
      setCopiedText(null);
      setTableOptions([]);
      setColumnOptions([]);
      setMetadataError("");
      setSelectedBuilderTable("");
      setActiveTab('config');
      setFieldSearch('');
    }
  }, [selectedNode, resolvedDefinitionNodeType]);

  const loadBuiltInTables = useCallback(async (scope, workspaceOverrideId) => {
    setMetadataLoading(prev => ({ ...prev, tables: true }));
    setMetadataError("");
    try {
      const token = localStorage.getItem('token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      const normalizedScope = scope || 'workspace';
      params.set('scope', normalizedScope);
      const effectiveWorkspaceId = workspaceOverrideId || currentWorkspaceId;
      if (normalizedScope === 'workspace' && effectiveWorkspaceId) {
        params.set('workspace_id', effectiveWorkspaceId);
      }
      const res = await fetch(`${API_BASE_URL}/tables?${params.toString()}`, { headers: authHeaders });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to load tables');
      }
      const payload = await res.json();
      const mapped = (Array.isArray(payload) ? payload : []).map((table) => ({
        value: String(table.id),
        label: table.name,
        schema: Array.isArray(table.schema) ? table.schema : [],
      }));
      setTableOptions(mapped);
    } catch (e) {
      setMetadataError(e.message || 'Failed to load tables');
      setTableOptions([]);
    } finally {
      setMetadataLoading(prev => ({ ...prev, tables: false }));
    }
  }, [currentWorkspaceId]);

  const loadExternalTables = useCallback(async (credentialId) => {
    setMetadataLoading(prev => ({ ...prev, tables: true }));
    setMetadataError("");
    try {
      const token = localStorage.getItem('token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/credentials/${credentialId}/db-metadata/tables`, { headers: authHeaders });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to load tables');
      }
      const payload = await res.json();
      const mapped = (Array.isArray(payload) ? payload : []).map((item) => ({
        value: item.full_name || item.name,
        label: item.full_name || item.name,
      }));
      setTableOptions(mapped);
    } catch (e) {
      setMetadataError(e.message || 'Failed to load tables');
      setTableOptions([]);
    } finally {
      setMetadataLoading(prev => ({ ...prev, tables: false }));
    }
  }, []);

  const loadExternalColumns = useCallback(async (credentialId, tableName) => {
    setMetadataLoading(prev => ({ ...prev, columns: true }));
    setMetadataError("");
    try {
      const token = localStorage.getItem('token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      params.set('table_name', tableName);
      const res = await fetch(`${API_BASE_URL}/credentials/${credentialId}/db-metadata/columns?${params.toString()}`, { headers: authHeaders });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to load columns');
      }
      const payload = await res.json();
      const mapped = (Array.isArray(payload) ? payload : []).map((item) => ({
        value: item.name,
        label: item.type ? `${item.name} (${item.type})` : item.name,
        name: item.name,
      }));
      setColumnOptions(mapped);
    } catch (e) {
      setMetadataError(e.message || 'Failed to load columns');
      setColumnOptions([]);
    } finally {
      setMetadataLoading(prev => ({ ...prev, columns: false }));
    }
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleDragStart = (e, text) => {
    e.dataTransfer.setData('text/plain', text);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleInputDrop = (e, currentValue, updateCallback) => {
    e.preventDefault();
    const text = e.dataTransfer.getData('text/plain');
    if (!text) return;
    
    const input = e.target;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = (currentValue || '').substring(0, start) + text + (currentValue || '').substring(end);
    
    updateCallback(newValue);
    
    // Restore focus and cursor
    setTimeout(() => {
        input.focus();
        input.selectionStart = input.selectionEnd = start + text.length;
    }, 0);
  };

  const handleRunNode = () => {
      // Create a context similar to backend
      const context = {
          nodes: {},
          env: {} // Add env vars if needed
      };
      
      // Populate context with completed nodes outputs
      nodes.forEach(node => {
          if (node.id !== selectedNode.id && nodeStatus && nodeStatus[node.id] && nodeStatus[node.id].status === 'completed') {
              const output = nodeStatus[node.id].output;
              context.nodes[node.id] = { data: output };
              if (node.data.label) {
                  context.nodes[node.data.label] = { data: output };
              }
          }
      });
      
      // Pass the node definition to allow running unsaved changes
      const nodeDefinition = {
          id: selectedNode.id,
          type: selectedNodeType,
          data: {
              ...selectedNode.data,
              // Ensure params are up to date
              params: formData,
              label: label
          }
      };
      
      runNode(selectedNode.id, context, nodeDefinition);
  };

  const prevNode = edges?.find(e => e.target === selectedNode?.id)
      ? nodes.find(n => n.id === edges.find(e => e.target === selectedNode?.id).source)
      : null;

  // Find all ancestor nodes (nodes that can reach the selected node)
  const getAncestors = (targetId, allNodes, allEdges) => {
      const ancestors = new Set();
      const queue = [targetId];
      const visited = new Set([targetId]);

      while (queue.length > 0) {
          const currentId = queue.shift();
          const incomingEdges = allEdges.filter(e => e.target === currentId);
          
          for (const edge of incomingEdges) {
              if (!visited.has(edge.source)) {
                  visited.add(edge.source);
                  ancestors.add(edge.source);
                  queue.push(edge.source);
              }
          }
      }
      return Array.from(ancestors).map(id => allNodes.find(n => n.id === id)).filter(Boolean);
  };

  const ancestorNodes = selectedNode ? getAncestors(selectedNode.id, nodes, edges || []) : [];

  const previewContext = React.useMemo(() => {
      const ctx = { nodes: {} };
      if (!nodes || !nodeStatus) return ctx;

      nodes.forEach(node => {
          if ((!selectedNode || node.id !== selectedNode.id) && nodeStatus[node.id] && nodeStatus[node.id].status === 'completed') {
              const output = nodeStatus[node.id].output;
              ctx.nodes[node.id] = { data: output };
              if (node.data.label) {
                  ctx.nodes[node.data.label] = { data: output };
              }
          }
      });
      
      if (prevNode && nodeStatus[prevNode.id] && nodeStatus[prevNode.id].status === 'completed') {
           const output = nodeStatus[prevNode.id].output;
           ctx.nodes['prev_node'] = { data: output };
      }

      // Always expose env so {{ env.var }} templates resolve without TypeError
      ctx.env = {};

      return ctx;
  }, [nodes, nodeStatus, selectedNode, prevNode]);


  if (!selectedNode) {
    return (
      <div style={{...containerStyle, width: `${width}px`}} ref={panelRef}>
        <div
            onMouseDown={startResizing}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '5px',
              height: '100%',
              cursor: 'col-resize',
              zIndex: 100,
              backgroundColor: isResizing ? 'var(--accent-color)' : 'transparent',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-color)'}
            onMouseLeave={(e) => !isResizing && (e.currentTarget.style.backgroundColor = 'transparent')}
          />
        <div style={{ color: 'var(--text-tertiary)', marginTop: '20px', textAlign: 'center', flex: 1 }}>
          Select a node to configure
        </div>
      </div>
    );
  }

  const nodeType = selectedNodeType;
  const isMaintenanceNode = ['maintenance', 'maintainance', 'maintenance_node'].includes(nodeType);

  const handleDelete = () => {
    deleteElements({ nodes: [{ id: selectedNode.id }] });
    if (setSelectedNode) {
      setSelectedNode(null);
    }
  };

  const updateReferences = (obj, oldLabel, newLabel) => {
    if (!obj) return obj;
    if (!oldLabel) return obj;

    if (typeof obj === 'string') {
        const escapedOld = oldLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Determine safe quote style for new label
        const useDouble = newLabel.includes("'") && !newLabel.includes('"');
        const quote = useDouble ? '"' : "'";
        const replacement = `nodes[${quote}${newLabel}${quote}]`;
        
        // Replace nodes['Old Label'] and nodes["Old Label"]
        let newStr = obj.replace(new RegExp(`nodes\\['${escapedOld}'\\]`, 'g'), replacement)
                        .replace(new RegExp(`nodes\\["${escapedOld}"\\]`, 'g'), replacement);
        
        // Replace nodes.OldLabel if it was a safe key
        if (/^[a-zA-Z0-9_]+$/.test(oldLabel)) {
             newStr = newStr.replace(new RegExp(`nodes\\.${escapedOld}(?![a-zA-Z0-9_])`, 'g'), replacement);
        }
        
        return newStr;
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => updateReferences(item, oldLabel, newLabel));
    }
    
    if (typeof obj === 'object') {
        const newObj = {};
        let changed = false;
        for (const key in obj) {
            const newVal = updateReferences(obj[key], oldLabel, newLabel);
            newObj[key] = newVal;
            if (newVal !== obj[key]) changed = true;
        }
        // If nothing changed, return original object reference to avoid unnecessary re-renders (optimization)
        if (!changed) return obj;
        return newObj;
    }
    
    return obj;
  };

  const handleLabelChange = (e) => {
    const newLabel = e.target.value;
    
    setLabel(newLabel);
    
    setNodes((nds) => {
      // Find the current label of the selected node from the fresh state
      // This is crucial because selectedNode.data.label might be stale during rapid typing
      const currentNode = nds.find((n) => n.id === selectedNode.id);
      const oldLabel = currentNode?.data?.label;

      return nds.map((node) => {
        // Update the selected node
        if (node.id === selectedNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              label: newLabel,
            },
          };
        }
        
        // Update references in other nodes
        if (oldLabel && oldLabel !== newLabel && node.data?.params) {
            const newParams = updateReferences(node.data.params, oldLabel, newLabel);
            
            if (JSON.stringify(newParams) !== JSON.stringify(node.data.params)) {
                 return {
                     ...node,
                     data: {
                         ...node.data,
                         params: newParams
                     }
                 };
            }
        }
        
        return node;
      });
    });
  };

  const handleUpdate = (newParams) => {
    setFormData(newParams);
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              params: newParams,
            },
          };
        }
        return node;
      })
    );
  };

  const handleChange = (field, value) => {
    handleUpdate({ ...formData, [field]: value });
  };

  const getToolConnectionKey = (tc, idx) => {
    const nodeId = tc?.node_id ? String(tc.node_id) : 'tool';
    const toolName = tc?.tool_name || tc?.node_type || `tool_${idx + 1}`;
    return `${nodeId}:${toolName}:${idx}`;
  };

  const updateToolConnectionDescription = (toolConnectionKey, description) => {
    const current = Array.isArray(formData.tool_connections) ? formData.tool_connections : [];
    let targetNodeId = '';
    const updatedToolConnections = current.map((tc, idx) => {
      if (getToolConnectionKey(tc, idx) !== toolConnectionKey) return tc;
      targetNodeId = tc?.node_id ? String(tc.node_id) : '';
      return { ...tc, description };
    });
    handleUpdate({ ...formData, tool_connections: updatedToolConnections });
    if (setEdges) {
      setEdges((eds) => eds.map((edge) => {
        const isToolEdge = edge?.data?.edge_type === 'tool';
        if (!isToolEdge) return edge;
        if (edge.source !== selectedNode.id || edge.target !== targetNodeId) return edge;
        return {
          ...edge,
          data: { ...(edge.data || {}), description }
        };
      }));
    }
  };

  useEffect(() => {
    const toolConnections = Array.isArray(formData.tool_connections) ? formData.tool_connections : [];
    if (toolConnections.length === 0) {
      if (selectedToolConnectionKey) setSelectedToolConnectionKey("");
      return;
    }
    const exists = toolConnections.some((tc, idx) => getToolConnectionKey(tc, idx) === selectedToolConnectionKey);
    if (!exists) {
      setSelectedToolConnectionKey(getToolConnectionKey(toolConnections[0], 0));
    }
  }, [formData.tool_connections, selectedToolConnectionKey]);

  const parseJsonObject = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return {};
    try {
      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  };

  const parseJsonArray = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return [];
    try {
      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const getHelperCredentialId = () => {
    const helperConfig = helperDbNodeCredentialFields[nodeType];
    if (!helperConfig) return '';
    const value = formData[helperConfig.field];
    return value ? String(value) : '';
  };

  useEffect(() => {
    if (!selectedNode) return;
    if (helperTableNodeTypes.has(nodeType) || nodeType === 'table_trigger') {
      loadBuiltInTables(formData.scope || 'workspace', formData.workspace_id ? String(formData.workspace_id) : currentWorkspaceId);
      return;
    }
    if (helperDbNodeTypes.has(nodeType)) {
      const credentialId = getHelperCredentialId();
      if (!credentialId) {
        setTableOptions([]);
        setColumnOptions([]);
        setSelectedBuilderTable("");
        return;
      }
      loadExternalTables(credentialId);
      return;
    }
    setTableOptions([]);
    setColumnOptions([]);
    setSelectedBuilderTable("");
  }, [selectedNode, nodeType, formData.scope, formData.workspace_id, formData.credential_id, currentWorkspaceId, loadBuiltInTables, loadExternalTables]);

  useEffect(() => {
    if (!selectedNode) return;
    if (helperTableNodeTypes.has(nodeType)) {
      const targetId = formData.table_id ? String(formData.table_id) : '';
      if (!targetId) {
        setColumnOptions([]);
        return;
      }
      const target = tableOptions.find(t => t.value === targetId);
      if (!target) {
        setColumnOptions([]);
        return;
      }
      const mapped = (target.schema || []).map(col => ({
        value: col.name,
        name: col.name,
        label: col.type ? `${col.name} (${col.type})` : col.name
      }));
      setColumnOptions(mapped);
      return;
    }
    if (helperDbNodeTypes.has(nodeType)) {
      const credentialId = getHelperCredentialId();
      const tableName = selectedBuilderTable || formData._qb_table || '';
      if (!credentialId || !tableName) {
        setColumnOptions([]);
        return;
      }
      loadExternalColumns(credentialId, tableName);
      return;
    }
    setColumnOptions([]);
  }, [selectedNode, nodeType, formData.table_id, formData._qb_table, selectedBuilderTable, tableOptions, loadExternalColumns]);

  const updateFilterRows = (nextFilters) => {
    handleChange('filters', nextFilters);
  };

  const renderFilterBuilder = () => {
    const rawFilters = formData.filters;
    const filters = Array.isArray(rawFilters) ? rawFilters : parseJsonArray(rawFilters);
    const operators = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'contains', 'in', 'not_in'];
    return (
      <div style={{ marginBottom: '12px' }}>
        {filters.map((item, index) => (
          <div key={`filter-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 110px auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <SearchableSelect
              style={{ ...inputStyle, marginBottom: 0 }}
              value={item?.field ? String(item.field) : ''}
              onChange={(val) => {
                const next = [...filters];
                next[index] = { ...next[index], field: val };
                updateFilterRows(next);
              }}
              options={columnOptions.map(c => ({ value: c.name || c.value, label: c.label }))}
              placeholder="Column"
            />
            <SearchableSelect
              style={{ ...inputStyle, marginBottom: 0 }}
              value={item?.op ? String(item.op) : 'eq'}
              onChange={(val) => {
                const next = [...filters];
                next[index] = { ...next[index], op: val };
                updateFilterRows(next);
              }}
              options={operators.map(op => ({ value: op, label: op }))}
              placeholder="Operator"
            />
            <div style={{ gridColumn: '1 / span 2' }}>
              <ExpressionInput
                value={item?.value ?? ''}
                onChange={(val) => {
                  const next = [...filters];
                  next[index] = { ...next[index], value: val };
                  updateFilterRows(next);
                }}
                placeholder="Value"
                ancestorNodes={ancestorNodes}
                nodeStatus={nodeStatus}
                envVars={envVars}
              />
            </div>
            <button
              style={{ gridColumn: '3', gridRow: '1 / span 2', alignSelf: 'stretch', padding: '4px 8px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', cursor: 'pointer' }}
              onClick={() => {
                const next = [...filters];
                next.splice(index, 1);
                updateFilterRows(next);
              }}
            >
              ×
            </button>
          </div>
        ))}
        <button
          style={{ padding: '4px 10px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          onClick={() => updateFilterRows([...(filters || []), { field: '', op: 'eq', value: '' }])}
        >
          + Filter
        </button>
      </div>
    );
  };

  const renderRowBuilder = (fieldName = 'row') => {
    const raw = formData[fieldName];
    const rowObject = typeof raw === 'object' && raw && !Array.isArray(raw) ? raw : parseJsonObject(raw);
    const entries = Object.entries(rowObject);
    return (
      <div style={{ marginBottom: '12px' }}>
        {entries.map(([key, value], index) => (
          <div key={`${fieldName}-entry-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <SearchableSelect
              style={{ ...inputStyle, marginBottom: 0 }}
              value={key}
              onChange={(val) => {
                const current = typeof formData[fieldName] === 'object' && formData[fieldName] ? { ...formData[fieldName] } : parseJsonObject(formData[fieldName]);
                const currentValue = current[key];
                delete current[key];
                current[val] = currentValue;
                handleChange(fieldName, current);
              }}
              options={columnOptions.map(c => ({ value: c.name || c.value, label: c.label }))}
              placeholder="Column"
            />
            <div style={{ gridColumn: '1 / span 1' }}>
              <ExpressionInput
                value={value ?? ''}
                onChange={(val) => {
                  const current = typeof formData[fieldName] === 'object' && formData[fieldName] ? { ...formData[fieldName] } : parseJsonObject(formData[fieldName]);
                  current[key] = val;
                  handleChange(fieldName, current);
                }}
                placeholder="Value"
                ancestorNodes={ancestorNodes}
                nodeStatus={nodeStatus}
                envVars={envVars}
              />
            </div>
            <button
              style={{ gridColumn: '2', gridRow: '1 / span 2', alignSelf: 'stretch', padding: '4px 8px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', cursor: 'pointer' }}
              onClick={() => {
                const current = typeof formData[fieldName] === 'object' && formData[fieldName] ? { ...formData[fieldName] } : parseJsonObject(formData[fieldName]);
                delete current[key];
                handleChange(fieldName, current);
              }}
            >
              ×
            </button>
          </div>
        ))}
        <button
          style={{ padding: '4px 10px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          onClick={() => {
            const current = typeof formData[fieldName] === 'object' && formData[fieldName] ? { ...formData[fieldName] } : parseJsonObject(formData[fieldName]);
            const available = columnOptions.map(c => c.name || c.value).find(name => !Object.keys(current).includes(name));
            const nextKey = available || `field_${Object.keys(current).length + 1}`;
            current[nextKey] = '';
            handleChange(fieldName, current);
          }}
        >
          + Field
        </button>
      </div>
    );
  };

  const applyDbBuilderQuery = (patch = {}) => {
    const source = { ...formData, ...patch };
    const mode = source._qb_mode || 'select';
    const tableName = source._qb_table || '';
    if (!tableName) return;
    const filters = Array.isArray(source._qb_filters) ? source._qb_filters : [];
    const whereParts = [];
    const params = {};
    let counter = 1;
    filters.forEach((flt) => {
      if (!flt?.field) return;
      const opMap = { eq: '=', ne: '!=', gt: '>', gte: '>=', lt: '<', lte: '<=' };
      const operator = opMap[flt.op] || '=';
      const pKey = `p${counter}`;
      counter += 1;
      whereParts.push(`${flt.field} ${operator} :${pKey}`);
      params[pKey] = flt.value;
    });
    const whereSql = whereParts.length ? ` WHERE ${whereParts.join(' AND ')}` : '';
    if (mode === 'select') {
      const selectedCols = Array.isArray(source._qb_select_columns) && source._qb_select_columns.length ? source._qb_select_columns.join(', ') : '*';
      const limit = Number(source._qb_limit) > 0 ? Number(source._qb_limit) : 100;
      handleUpdate({ ...source, query: `SELECT ${selectedCols} FROM ${tableName}${whereSql} LIMIT ${limit}`, parameters: params });
      return;
    }
    if (mode === 'insert') {
      const insertValues = typeof source._qb_insert_values === 'object' && source._qb_insert_values ? source._qb_insert_values : {};
      const columns = Object.keys(insertValues).filter(Boolean);
      if (columns.length === 0) return;
      const placeholders = columns.map((col, idx) => `:v${idx + 1}`);
      columns.forEach((col, idx) => {
        params[`v${idx + 1}`] = insertValues[col];
      });
      handleUpdate({ ...source, query: `INSERT INTO ${tableName} (${columns.join(', ')}) VALUES (${placeholders.join(', ')})`, parameters: params });
      return;
    }
    if (mode === 'update') {
      const updateValues = typeof source._qb_update_values === 'object' && source._qb_update_values ? source._qb_update_values : {};
      const columns = Object.keys(updateValues).filter(Boolean);
      if (columns.length === 0) return;
      const setClauses = columns.map((col, idx) => `${col} = :u${idx + 1}`);
      columns.forEach((col, idx) => {
        params[`u${idx + 1}`] = updateValues[col];
      });
      const query = `UPDATE ${tableName} SET ${setClauses.join(', ')}${whereSql}`;
      handleUpdate({ ...source, query, parameters: params });
    }
  };

  const renderNodeHelperBuilder = () => {
    const isTableNode = helperTableNodeTypes.has(nodeType);
    const isDbNode = helperDbNodeTypes.has(nodeType);
    if (!isTableNode && !isDbNode) return null;
    const tableAction = isTableNode ? String(formData.action || tableActionByLegacyType[nodeType] || 'query') : '';
    const tableSelectorValue = isTableNode ? (formData.table_id ? String(formData.table_id) : '') : (selectedBuilderTable || formData._qb_table || '');
    const selectedCols = Array.isArray(formData.select) ? formData.select : parseJsonArray(formData.select);
    const helperTitle = isTableNode ? 'Table Helper Builder' : 'SQL Helper Builder';
    return (
      <div style={{ marginBottom: '16px', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px', background: 'var(--bg-tertiary)' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>{helperTitle}</div>
        <div style={{ marginBottom: '8px', fontSize: '11px', color: 'var(--text-secondary)' }}>
          {metadataLoading.tables || metadataLoading.columns ? 'Loading metadata...' : (metadataError || '')}
        </div>
        <label style={{ ...labelStyle, marginBottom: '4px' }}>Table</label>
        <SearchableSelect
          style={{ ...inputStyle, marginBottom: '8px' }}
          value={tableSelectorValue}
          onChange={(val) => {
            if (isTableNode) {
              const next = { ...formData, table_id: val };
              if (tableAction === 'query') {
                next.select = [];
              }
              handleUpdate(next);
              return;
            }
            setSelectedBuilderTable(val);
            const nextPatch = {
              _qb_table: val,
              _qb_select_columns: [],
              _qb_select_last: ''
            };
            handleUpdate({ ...formData, ...nextPatch });
            applyDbBuilderQuery(nextPatch);
          }}
          options={tableOptions.map(t => ({ value: t.value, label: t.label }))}
          placeholder="-- Select Table --"
        />
        {isTableNode && (tableAction === 'query' || tableAction === 'delete') && renderFilterBuilder()}
        {isTableNode && tableAction === 'query' && (
          <>
            <label style={{ ...labelStyle, marginBottom: '4px' }}>Select Columns</label>
            <SearchableSelect
              style={{ ...inputStyle, marginBottom: '8px' }}
              value=""
              onChange={(val) => {
                if (!val) return;
                const next = selectedCols.includes(val) ? selectedCols : [...selectedCols, val];
                handleChange('select', next);
              }}
              options={columnOptions.map(c => ({ value: c.name || c.value, label: c.label }))}
              placeholder="Add column"
            />
            {selectedCols.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {selectedCols.map((col) => (
                  <button
                    key={col}
                    style={{ padding: '2px 8px', borderRadius: '999px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '11px' }}
                    onClick={() => handleChange('select', selectedCols.filter(item => item !== col))}
                  >
                    {col} ×
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {isTableNode && (tableAction === 'insert' || tableAction === 'update' || tableAction === 'upsert') && renderRowBuilder('row')}
        {isDbNode && (
          <>
            <label style={{ ...labelStyle, marginBottom: '4px' }}>Mode</label>
            <SearchableSelect
              style={{ ...inputStyle, marginBottom: '8px' }}
              value={formData._qb_mode || 'select'}
              onChange={(val) => {
                handleChange('_qb_mode', val);
                applyDbBuilderQuery({ _qb_mode: val });
              }}
              options={[
                { value: 'select', label: 'Select' },
                { value: 'insert', label: 'Insert' },
                { value: 'update', label: 'Update' },
              ]}
              placeholder="-- Select Mode --"
            />
            {(formData._qb_mode || 'select') === 'select' && (
              <>
                <label style={{ ...labelStyle, marginBottom: '4px' }}>Select Columns</label>
                <SearchableSelect
                  style={{ ...inputStyle, marginBottom: '8px' }}
                  value={formData._qb_select_last || ''}
                  onChange={(val) => {
                    if (!val) return;
                    const current = Array.isArray(formData._qb_select_columns) ? formData._qb_select_columns : [];
                    const next = current.includes(val) ? current : [...current, val];
                    handleChange('_qb_select_last', val);
                    handleChange('_qb_select_columns', next);
                    applyDbBuilderQuery({ _qb_select_columns: next });
                  }}
                  options={columnOptions.map(c => ({ value: c.name || c.value, label: c.label }))}
                  placeholder="Add column"
                />
                {(() => {
                  const selectedDbCols = Array.isArray(formData._qb_select_columns) ? formData._qb_select_columns : [];
                  if (selectedDbCols.length === 0) return null;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                      {selectedDbCols.map((col) => (
                        <button
                          key={`db-select-col-${col}`}
                          style={{ padding: '2px 8px', borderRadius: '999px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '11px' }}
                          onClick={() => {
                            const next = selectedDbCols.filter(item => item !== col);
                            handleChange('_qb_select_columns', next);
                            applyDbBuilderQuery({ _qb_select_columns: next });
                          }}
                        >
                          {col} ×
                        </button>
                      ))}
                    </div>
                  );
                })()}
                {(() => {
                  const filters = Array.isArray(formData._qb_filters) ? formData._qb_filters : [];
                  return (
                    <div style={{ marginBottom: '8px' }}>
                      {filters.map((item, index) => (
                        <div key={`qb-select-filter-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 110px auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                          <SearchableSelect
                            style={{ ...inputStyle, marginBottom: 0 }}
                            value={item?.field || ''}
                            onChange={(val) => {
                              const next = [...filters];
                              next[index] = { ...next[index], field: val };
                              handleChange('_qb_filters', next);
                              applyDbBuilderQuery({ _qb_filters: next });
                            }}
                            options={columnOptions.map(c => ({ value: c.name || c.value, label: c.label }))}
                            placeholder="Column"
                          />
                          <SearchableSelect
                            style={{ ...inputStyle, marginBottom: 0 }}
                            value={item?.op || 'eq'}
                            onChange={(val) => {
                              const next = [...filters];
                              next[index] = { ...next[index], op: val };
                              handleChange('_qb_filters', next);
                              applyDbBuilderQuery({ _qb_filters: next });
                            }}
                            options={['eq', 'ne', 'gt', 'gte', 'lt', 'lte'].map(op => ({ value: op, label: op }))}
                            placeholder="Operator"
                          />
                          <div style={{ gridColumn: '1 / span 2' }}>
                            <ExpressionInput
                              value={item?.value ?? ''}
                              onChange={(val) => {
                                const next = [...filters];
                                next[index] = { ...next[index], value: val };
                                handleChange('_qb_filters', next);
                                applyDbBuilderQuery({ _qb_filters: next });
                              }}
                              placeholder="Value"
                              ancestorNodes={ancestorNodes}
                              nodeStatus={nodeStatus}
                              envVars={envVars}
                            />
                          </div>
                          <button
                            style={{ gridColumn: '3', gridRow: '1 / span 2', alignSelf: 'stretch', padding: '4px 8px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => {
                              const next = [...filters];
                              next.splice(index, 1);
                              handleChange('_qb_filters', next);
                              applyDbBuilderQuery({ _qb_filters: next });
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        style={{ padding: '4px 10px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginBottom: '8px' }}
                        onClick={() => {
                          const next = [...filters, { field: '', op: 'eq', value: '' }];
                          handleChange('_qb_filters', next);
                          applyDbBuilderQuery({ _qb_filters: next });
                        }}
                      >
                        + Filter
                      </button>
                    </div>
                  );
                })()}
                <input
                  style={{ ...inputStyle, marginBottom: '8px' }}
                  type="number"
                  value={formData._qb_limit || 100}
                  onChange={(e) => {
                    const next = parseInt(e.target.value, 10) || 100;
                    handleChange('_qb_limit', next);
                    applyDbBuilderQuery({ _qb_limit: next });
                  }}
                  placeholder="Limit"
                />
              </>
            )}
            {(formData._qb_mode || 'select') === 'insert' && renderRowBuilder('_qb_insert_values')}
            {(formData._qb_mode || 'select') === 'update' && (
              <>
                {renderRowBuilder('_qb_update_values')}
                {(() => {
                  const filters = Array.isArray(formData._qb_filters) ? formData._qb_filters : [];
                  return (
                    <div style={{ marginBottom: '8px' }}>
                      {filters.map((item, index) => (
                        <div key={`qb-filter-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 110px auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                          <SearchableSelect
                            style={{ ...inputStyle, marginBottom: 0 }}
                            value={item?.field || ''}
                            onChange={(val) => {
                              const next = [...filters];
                              next[index] = { ...next[index], field: val };
                              handleChange('_qb_filters', next);
                              applyDbBuilderQuery({ _qb_filters: next });
                            }}
                            options={columnOptions.map(c => ({ value: c.name || c.value, label: c.label }))}
                            placeholder="Column"
                          />
                          <SearchableSelect
                            style={{ ...inputStyle, marginBottom: 0 }}
                            value={item?.op || 'eq'}
                            onChange={(val) => {
                              const next = [...filters];
                              next[index] = { ...next[index], op: val };
                              handleChange('_qb_filters', next);
                              applyDbBuilderQuery({ _qb_filters: next });
                            }}
                            options={['eq', 'ne', 'gt', 'gte', 'lt', 'lte'].map(op => ({ value: op, label: op }))}
                            placeholder="Operator"
                          />
                          <div style={{ gridColumn: '1 / span 2' }}>
                            <ExpressionInput
                              value={item?.value ?? ''}
                              onChange={(val) => {
                                const next = [...filters];
                                next[index] = { ...next[index], value: val };
                                handleChange('_qb_filters', next);
                                applyDbBuilderQuery({ _qb_filters: next });
                              }}
                              placeholder="Value"
                              ancestorNodes={ancestorNodes}
                              nodeStatus={nodeStatus}
                              envVars={envVars}
                            />
                          </div>
                          <button
                            style={{ gridColumn: '3', gridRow: '1 / span 2', alignSelf: 'stretch', padding: '4px 8px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => {
                              const next = [...filters];
                              next.splice(index, 1);
                              handleChange('_qb_filters', next);
                              applyDbBuilderQuery({ _qb_filters: next });
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        style={{ padding: '4px 10px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                        onClick={() => {
                          const next = [...filters, { field: '', op: 'eq', value: '' }];
                          handleChange('_qb_filters', next);
                          applyDbBuilderQuery({ _qb_filters: next });
                        }}
                      >
                        + Filter
                      </button>
                    </div>
                  );
                })()}
              </>
            )}
            <button
              style={{ padding: '5px 10px', background: 'var(--accent-color)', border: '1px solid var(--accent-color)', color: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
              onClick={() => applyDbBuilderQuery({})}
            >
              Generate SQL
            </button>
          </>
        )}
      </div>
    );
  };


  const renderNestedKeys = (obj, prefix) => {
    if (!obj || typeof obj !== 'object') return null;

    return Object.keys(obj).map(key => {
        const value = obj[key];
        const isSafeKey = /^[a-zA-Z0-9_]+$/.test(key);
        const path = isSafeKey ? `${prefix}.${key}` : `${prefix}['${key}']`;

        const isObject = value && typeof value === 'object' && !Array.isArray(value);
        
        let displayValue = '';
        if (value !== null && value !== undefined && !isObject && typeof value !== 'function') {
            const strVal = String(value);
            displayValue = strVal.length > 20 ? strVal.substring(0, 20) + '...' : strVal;
        }

        return (
            <div key={path}>
                <div 
                    style={{ fontSize: '11px', color: 'var(--text-secondary)', cursor: 'grab', padding: '2px 0', display: 'flex', alignItems: 'center' }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, `{{ ${path} }}`)}
                    onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(`{{ ${path} }}`);
                    }}
                    title={`Click to copy, drag to insert ${key} reference`}
                >
                    <span style={{ marginRight: '5px' }}>- {key}</span>
                    {displayValue && <span style={{ color: 'var(--text-tertiary)' }}>: {displayValue}</span>}
                </div>
                {isObject && (
                    <div style={{ paddingLeft: '10px', borderLeft: '1px solid var(--border-color)' }}>
                        {renderNestedKeys(value, path)}
                    </div>
                )}
            </div>
        );
    });
  };

  const renderCredentialSelector = (serviceType, isMandatory = false, fieldName = 'credential_id', label = null) => {
    // Filter credentials by service type; show all when no type specified or "any"
    const relevantCreds = (serviceType && serviceType !== 'any')
      ? credentials.filter(c => c.service_type === serviceType)
      : credentials;
    const displayLabel = label || (isMandatory ? 'Credential' : 'Credential (Optional)');

    // Build credential options with health status dots
    const credOptions = [
      ...relevantCreds.map(c => {
        let dot = '';
        if (c.oauth_managed) {
          if (!c.token_expires_at) {
            dot = '\u{1F7E2} '; // green circle
          } else {
            const hoursLeft = (new Date(c.token_expires_at) - new Date()) / (1000 * 60 * 60);
            if (hoursLeft <= 0) dot = '\u{1F534} '; // red circle
            else if (hoursLeft <= 24) dot = '\u{1F7E1} '; // yellow circle
            else dot = '\u{1F7E2} '; // green circle
          }
        }
        return { value: String(c.id), label: `${dot}${c.name}` };
      }),
      { value: 'NEW', label: '+ Create New Connection' },
    ];

    // Check if selected credential is expired OAuth for reconnect hint
    const selectedCredId = formData[fieldName] ? String(formData[fieldName]) : '';
    const selectedCred = selectedCredId ? relevantCreds.find(c => String(c.id) === selectedCredId) : null;
    const isExpired = selectedCred?.oauth_managed && selectedCred?.token_expires_at &&
      new Date(selectedCred.token_expires_at) < new Date();

    // Check for inline OAuth connect availability
    const oauthProviderName = SERVICE_TYPE_OAUTH_MAP[serviceType];
    const oauthProviderEntry = oauthProviderName
      ? oauthHook.oauthProviders.find(p => p.provider === oauthProviderName && p.available)
      : null;

    return (
      <div style={{ marginBottom: '15px' }}>
        <label style={labelStyle}>{displayLabel}</label>
        <SearchableSelect
          style={inputStyle}
          value={selectedCredId}
          onChange={(val) => {
            if (val === 'NEW') {
              if (oauthProviderEntry) {
                oauthHook.connect(oauthProviderName);
              } else if (onCreateCredential) {
                onCreateCredential(serviceType);
              }
            } else {
              handleChange(fieldName, val);
            }
          }}
          options={credOptions}
          placeholder={isMandatory ? '-- Select Credential (Required) --' : '-- Select Credential --'}
        />
        {/* Inline OAuth connect button when no credential is selected */}
        {!selectedCredId && oauthProviderEntry && (
          <div style={{
            marginTop: '6px', padding: '8px 12px', borderRadius: '6px',
            background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <button
              type="button"
              disabled={oauthHook.connecting}
              onClick={() => oauthHook.connect(oauthProviderName)}
              style={{
                background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px',
                padding: '5px 14px', fontSize: '12px', cursor: oauthHook.connecting ? 'wait' : 'pointer',
                opacity: oauthHook.connecting ? 0.7 : 1, whiteSpace: 'nowrap',
              }}
            >
              {oauthHook.connecting ? 'Connecting...' : `Connect with ${oauthProviderEntry.label}`}
            </button>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
              One-click OAuth — no manual tokens needed
            </span>
          </div>
        )}
        {/* Empty state guidance when no OAuth provider available */}
        {!selectedCredId && !oauthProviderEntry && relevantCreds.length === 0 && oauthProviderName && (
          <div style={{
            fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '6px', padding: '8px 10px',
            background: 'rgba(234,179,8,0.06)', border: '1px dashed rgba(234,179,8,0.3)', borderRadius: '4px',
          }}>
            No credentials found. Click <strong>"+ Create New Connection"</strong> above to add your{' '}
            {oauthProviderName.charAt(0).toUpperCase() + oauthProviderName.slice(1)} credentials — you'll
            see step-by-step instructions for getting your API key or Client ID.
          </div>
        )}
        {isExpired && (
          <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Token expired.</span>
            <button
              type="button"
              onClick={() => {
                if (oauthProviderEntry) oauthHook.connect(oauthProviderName, selectedCred?.name, selectedCredId);
                else if (onCreateCredential) onCreateCredential(serviceType);
              }}
              style={{ background: '#d97706', color: '#fff', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer' }}
            >
              Reconnect
            </button>
          </div>
        )}
        {oauthHook.error && !selectedCredId && (
          <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>{oauthHook.error}</div>
        )}
        {formData[fieldName] && !isMandatory && !isExpired && (
           <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '-10px', marginBottom: '15px' }}>
             Using stored credential. Manual fields below will be overridden/merged.
           </div>
        )}
      </div>
    );
  };

  const renderDynamicFields = (definition) => {
    if (!definition || !Array.isArray(definition.fields) || definition.fields.length === 0) {
      const fallbackCredentialMap = {
        kafka: { serviceType: 'kafka', required: true },
        queue_bridge: { serviceType: 'aws_sqs', required: true },
        redis: { serviceType: 'redis', required: true },
        object_storage: { serviceType: 'object_storage', required: true }
      };
      const fallback = fallbackCredentialMap[definition?.type];
      return fallback ? renderCredentialSelector(fallback.serviceType, fallback.required) : null;
    }

    // §4.79 Multi-Agent Orchestration Studio — agent-graph-only fields are hidden in standard workflows
    const AGENT_GRAPH_ONLY_FIELDS = new Set([
      'agent_role',
      'shared_memory_read',
      'shared_memory_write',
      'memory_write_key',
      'memory_keys_to_load',
      'vector_memory_query',
    ]);

    const isFieldVisible = (field) => {
      if (workflowType !== 'agent_graph' && AGENT_GRAPH_ONLY_FIELDS.has(field.name)) return false;
      if (!field.show_when) return true;
      const conds = Array.isArray(field.show_when) ? field.show_when : [field.show_when];
      return conds.every(({ field: watchField, values }) => {
        const watchDef = definition.fields.find(f => f.name === watchField);
        const current = String(formData[watchField] ?? watchDef?.default ?? '');
        return values.includes(current);
      });
    };

    const isFieldRequired = (field) => {
      if (field.required) return true;
      if (!field.required_when) return false;
      const conds = Array.isArray(field.required_when) ? field.required_when : [field.required_when];
      return conds.every(({ field: watchField, values }) => {
        const watchDef = definition.fields.find(f => f.name === watchField);
        const current = String(formData[watchField] ?? watchDef?.default ?? '');
        return values.includes(current);
      });
    };

    const totalFieldCount = (definition.fields || []).filter(isFieldVisible).filter(f => !(resolvedDefinitionNodeType === 'if' && f.name === 'logic')).length;
    const searchQuery = fieldSearch.trim().toLowerCase();
    const matchesSearch = (field) => {
      if (!searchQuery) return true;
      const hay = `${field.label || ''} ${field.name || ''} ${field.help || field.description || ''}`.toLowerCase();
      return hay.includes(searchQuery);
    };

    return (
      <div>
        <div style={{ marginBottom: '15px', color: 'var(--text-tertiary)', fontSize: '12px', fontStyle: 'italic' }}>
          {definition.description}
        </div>
        {totalFieldCount >= 8 && (
          <div style={{ marginBottom: '12px', position: 'relative' }}>
            <input
              type="text"
              placeholder={`Search ${totalFieldCount} fields…`}
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
              style={{
                ...inputStyle,
                marginBottom: 0,
                paddingLeft: '30px',
                fontSize: '12px'
              }}
            />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', fontSize: '12px', pointerEvents: 'none' }}>⌕</span>
            {fieldSearch && (
              <button
                onClick={() => setFieldSearch('')}
                title="Clear search"
                style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '14px', padding: '2px 6px' }}
              >×</button>
            )}
          </div>
        )}
        {definition.fields.filter(isFieldVisible).filter(field => {
          // ConditionBuilder handles 'logic' internally for 'if' nodes
          if (resolvedDefinitionNodeType === 'if' && field.name === 'logic') return false;
          return matchesSearch(field);
        }).map((field) => (
          <div key={field.name}>
            {field.type !== 'credential' && field.type !== 'model_cheat_sheet' && field.type !== 'schema_browser' && field.label && (
              <label style={labelStyle}>
                {field.label}
                {isFieldRequired(field) && <span style={requiredMarkStyle} title="Required">*</span>}
              </label>
            )}
            {(field.help || field.description) && field.type !== 'model_cheat_sheet' && field.type !== 'schema_browser' && (
              <span style={helpStyle}>{field.help || field.description}</span>
            )}
            {field.type === 'credential' ? (
              renderCredentialSelector(field.service_type, Boolean(field.required), field.name, field.label)
            ) : (resolvedDefinitionNodeType === 'if' && field.name === 'conditions') ? (
              <ConditionBuilder
                conditions={Array.isArray(formData.conditions) ? formData.conditions : []}
                logic={formData.logic || 'all'}
                onChange={(newConditions, newLogic) => handleUpdate({ ...formData, conditions: newConditions, logic: newLogic })}
                ancestorNodes={ancestorNodes}
                nodeStatus={nodeStatus}
                envVars={envVars}
              />
            ) : (field.name === 'workspace_id' && (resolvedDefinitionNodeType === 'table' || resolvedDefinitionNodeType === 'table_trigger')) ? (
              <SearchableSelect
                style={inputStyle}
                value={formData.workspace_id ? String(formData.workspace_id) : ''}
                onChange={(val) => handleUpdate({ ...formData, workspace_id: val, table_id: '' })}
                options={workspaceOptions.map((workspace) => ({
                  value: String(workspace.id),
                  label: workspace.name,
                }))}
                placeholder={workspaceOptions.length ? '-- Select Workspace --' : '-- No Workspaces --'}
              />
            ) : (field.name === 'table_id' && (resolvedDefinitionNodeType === 'table' || resolvedDefinitionNodeType === 'table_trigger')) ? (
              <SearchableSelect
                style={inputStyle}
                value={formData.table_id ? String(formData.table_id) : ''}
                onChange={(val) => handleChange('table_id', val)}
                options={tableOptions.map((table) => ({ value: table.value, label: table.label }))}
                placeholder={metadataLoading.tables ? '-- Loading Tables --' : '-- Select Table --'}
              />
            ) : field.type === 'select' ? (
              <SearchableSelect
                style={inputStyle}
                value={formData[field.name] || field.default || ''}
                onChange={(val) => handleChange(field.name, val)}
                options={(field.options || []).map(opt => {
                  const val = typeof opt === 'object' ? opt.value : opt;
                  const lbl = typeof opt === 'object' ? opt.label : opt;
                  return { value: val, label: lbl };
                })}
                placeholder="-- Select --"
              />
            ) : field.type === 'textarea' ? (
              <ExpressionInput
                multiline
                value={formData[field.name] || field.default || ''}
                onChange={(val) => handleChange(field.name, val)}
                onDrop={(e) => handleInputDrop(e, formData[field.name], (val) => handleChange(field.name, val))}
                onDragOver={(e) => e.preventDefault()}
                placeholder={field.placeholder || ''}
                ancestorNodes={ancestorNodes}
                nodeStatus={nodeStatus}
                envVars={envVars}
                fieldLabel={field.label}
              />
            ) : field.type === 'json' ? (
              <JsonEditor
                value={(() => { const v = formData[field.name] ?? field.default ?? ''; return typeof v === 'string' ? v : JSON.stringify(v, null, 2); })()}
                onChange={(val) => handleChange(field.name, val)}
                onDrop={(e) => handleInputDrop(e, formData[field.name], (val) => handleChange(field.name, val))}
                onDragOver={(e) => e.preventDefault()}
                placeholder={field.placeholder || ''}
                ancestorNodes={ancestorNodes}
                nodeStatus={nodeStatus}
                envVars={envVars}
              />
            ) : field.type === 'number' ? (
              <input
                style={inputStyle}
                type="number"
                value={formData[field.name] || field.default || 0}
                onChange={(e) => handleChange(field.name, parseFloat(e.target.value))}
                placeholder={field.placeholder || ''}
              />
            ) : field.type === 'boolean' ? (
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="checkbox"
                  checked={Boolean(formData[field.name] ?? field.default)}
                  onChange={(e) => handleChange(field.name, e.target.checked)}
                />
              </div>
            ) : field.type === 'workflow' ? (
              <SearchableSelect
                style={inputStyle}
                value={formData[field.name] ? String(formData[field.name]) : ''}
                onChange={(val) => handleChange(field.name, val)}
                options={workflowOptions.map(wf => ({ value: String(wf.id), label: wf.name }))}
                placeholder="-- Select Workflow --"
              />
            ) : field.type === 'case_type_picker' ? (
              <SearchableSelect
                style={inputStyle}
                value={formData[field.name] ? String(formData[field.name]) : ''}
                onChange={(val) => handleChange(field.name, val)}
                options={[
                  { value: '', label: 'All case types' },
                  ...caseTypeOptions.map(ct => ({ value: String(ct.id), label: `${ct.icon || '📋'} ${ct.name}` }))
                ]}
                placeholder={caseTypeOptions.length === 0 ? '-- No case types found --' : (field.placeholder || '-- Select Case Type --')}
              />
            ) : field.type === 'case_task_type_picker' ? (() => {
              const caseTypeFilter = field.case_type_source_field ? String(formData[field.case_type_source_field] || '') : '';
              const filteredTaskTypes = caseTypeFilter
                ? discretionaryTaskTypeOptions.filter(tt => String(tt.case_type_id || '') === caseTypeFilter)
                : discretionaryTaskTypeOptions;
              const showCaseTypeSuffix = !caseTypeFilter;
              return (
                <SearchableSelect
                  style={inputStyle}
                  value={formData[field.name] ? String(formData[field.name]) : ''}
                  onChange={(val) => handleChange(field.name, val)}
                  options={[
                    { value: '', label: field.placeholder || '-- None (ad-hoc task) --' },
                    ...filteredTaskTypes.map(tt => {
                      const iconPrefix = tt.icon && tt.icon !== 'checkbox' && tt.icon !== 'step' ? `${tt.icon} ` : '';
                      return {
                        value: String(tt.id),
                        label: `${iconPrefix}${tt.name}${showCaseTypeSuffix && tt.case_type_name ? ` — ${tt.case_type_name}` : ''}`,
                      };
                    })
                  ]}
                  placeholder={filteredTaskTypes.length === 0 ? (caseTypeFilter ? '-- No discretionary task types for this case type --' : '-- No discretionary task types configured --') : (field.placeholder || '-- Select Task Type --')}
                />
              );
            })() : field.type === 'team_select' ? (
              <SearchableSelect
                style={inputStyle}
                value={formData[field.name] ? String(formData[field.name]) : ''}
                onChange={(val) => handleChange(field.name, val)}
                options={[
                  { value: '', label: '-- None --' },
                  ...teamOptions.map(t => ({ value: String(t.id), label: t.name }))
                ]}
                placeholder={teamOptions.length === 0 ? '-- No teams found --' : (field.placeholder || '-- Select Team --')}
              />
            ) : field.type === 'role_select' ? (
              <SearchableSelect
                style={inputStyle}
                value={formData[field.name] ? String(formData[field.name]) : ''}
                onChange={(val) => handleChange(field.name, val)}
                options={[
                  { value: '', label: '-- None --' },
                  ...roleOptions.map(r => ({ value: r.name, label: r.name }))
                ]}
                placeholder={roleOptions.length === 0 ? '-- No roles defined --' : (field.placeholder || '-- Select Role --')}
              />
            ) : field.type === 'password' ? (
              <input
                style={inputStyle}
                type="password"
                value={formData[field.name] || field.default || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder || ''}
              />
            ) : field.type === 'attachment' ? (
              <AttachmentSelector
                value={formData[field.name] || ''}
                onChange={(val) => handleChange(field.name, val)}
                context={ancestorNodes}
              />
            ) : field.type === 'code' ? (
              <CodeEditor
                value={formData[field.name] || field.default || ''}
                onChange={(val) => handleChange(field.name, val)}
                language={formData.language || 'python'}
                placeholder={field.placeholder || ''}
                ancestorNodes={ancestorNodes}
                nodeStatus={nodeStatus}
                envVars={envVars}
              />
            ) : field.type === 'array_of_strings' ? (
              <div style={{ marginBottom: '15px' }}>
                {(Array.isArray(formData[field.name]) ? formData[field.name] : (formData[field.name] ? String(formData[field.name]).split(',').map(s => s.trim()).filter(Boolean) : [])).map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <input
                      style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const arr = [...(Array.isArray(formData[field.name]) ? formData[field.name] : [])];
                        arr[idx] = e.target.value;
                        handleChange(field.name, arr);
                      }}
                      placeholder={field.placeholder || `Case ${idx + 1}`}
                    />
                    <button
                      style={{ padding: '4px 8px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer' }}
                      onClick={() => {
                        const arr = Array.isArray(formData[field.name]) ? [...formData[field.name]] : [];
                        arr.splice(idx, 1);
                        handleChange(field.name, arr);
                      }}
                    >×</button>
                  </div>
                ))}
                <button
                  style={{ padding: '4px 10px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  onClick={() => {
                    const arr = Array.isArray(formData[field.name]) ? [...formData[field.name]] : [];
                    arr.push('');
                    handleChange(field.name, arr);
                  }}
                >+ Add Item</button>
              </div>
            ) : field.type === 'form_builder' ? (
              <FormBuilder
                value={formData[field.name]}
                onChange={(val) => handleChange(field.name, val)}
                fieldCatalog={fieldCatalog}
              />
            ) : field.type === 'nested_array' ? (() => {
              const items = Array.isArray(formData[field.name]) ? formData[field.name] : [];
              const uniqField = field.unique_field;
              const dupKeys = new Set();
              if (uniqField) {
                const seen = new Set();
                items.forEach(it => {
                  const v = String(it?.[uniqField] ?? '').trim();
                  if (!v) return;
                  if (seen.has(v)) dupKeys.add(v);
                  seen.add(v);
                });
              }
              const previewStyleField = field.preview_style_field || 'style';
              const previewLabelField = field.preview_label_field || 'label';
              const previewValueField = field.preview_value_field || 'value';
              const styleToBg = (s) => (
                s === 'success' ? '#22c55e' :
                s === 'danger' ? '#ef4444' :
                s === 'warning' ? '#f59e0b' :
                'var(--accent-color)'
              );
              return (
              <div style={{ marginBottom: '15px' }}>
                {items.map((item, idx) => {
                  const itemDupVal = uniqField ? String(item?.[uniqField] ?? '').trim() : '';
                  const isDup = uniqField && itemDupVal && dupKeys.has(itemDupVal);
                  return (
                  <div key={idx} style={{ background: 'var(--bg-tertiary)', border: `1px solid ${isDup ? '#ef4444' : 'var(--border-color)'}`, borderRadius: '4px', padding: '10px', marginBottom: '8px', position: 'relative' }}>
                    {(field.schema || []).filter(sf => {
                      if (!sf.show_when) return true;
                      const conds = Array.isArray(sf.show_when) ? sf.show_when : [sf.show_when];
                      return conds.every(({ field: wf, values }) => values.includes(String(item[wf] ?? sf.default ?? '')));
                    }).map(subField => (
                      <div key={subField.name} style={{ marginBottom: '6px' }}>
                        <label style={{ ...labelStyle, fontSize: '11px', marginBottom: '3px' }}>{subField.label}</label>
                        {subField.type === 'select' ? (
                          <SearchableSelect
                            style={{ ...inputStyle, marginBottom: '4px' }}
                            value={item[subField.name] !== undefined ? String(item[subField.name]) : (subField.default || '')}
                            onChange={(val) => {
                              const arr = [...(Array.isArray(formData[field.name]) ? formData[field.name] : [])];
                              arr[idx] = { ...arr[idx], [subField.name]: val };
                              handleChange(field.name, arr);
                            }}
                            options={(subField.options || []).map(o => typeof o === 'object' ? o : { value: o, label: o })}
                            placeholder="-- Select --"
                          />
                        ) : (
                          <ExpressionInput
                            value={item[subField.name] || ''}
                            onChange={(val) => {
                              const arr = [...(Array.isArray(formData[field.name]) ? formData[field.name] : [])];
                              arr[idx] = { ...arr[idx], [subField.name]: val };
                              handleChange(field.name, arr);
                            }}
                            placeholder={subField.placeholder || ''}
                            ancestorNodes={ancestorNodes}
                            nodeStatus={nodeStatus}
                            envVars={envVars}
                          />
                        )}
                      </div>
                    ))}
                    {isDup && (
                      <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                        Duplicate {uniqField} — values must be unique.
                      </div>
                    )}
                    <button
                      style={{ position: 'absolute', top: '6px', right: '6px', padding: '2px 6px', background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '16px' }}
                      onClick={() => {
                        const arr = [...(Array.isArray(formData[field.name]) ? formData[field.name] : [])];
                        arr.splice(idx, 1);
                        handleChange(field.name, arr);
                      }}
                    >×</button>
                  </div>
                  );
                })}
                <button
                  style={{ padding: '4px 10px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  onClick={() => {
                    const arr = [...(Array.isArray(formData[field.name]) ? formData[field.name] : [])];
                    const newItem = {};
                    (field.schema || []).forEach(sf => { newItem[sf.name] = sf.default !== undefined ? sf.default : ''; });
                    arr.push(newItem);
                    handleChange(field.name, arr);
                  }}
                >+ Add Row</button>
                {field.preview === 'buttons' && items.length > 0 && (
                  <div style={{ marginTop: '12px', padding: '10px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Preview — what the worker sees</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {items.map((it, i) => {
                        const label = String(it[previewLabelField] || it[previewValueField] || '').trim() || '(no label)';
                        const bg = styleToBg(it[previewStyleField]);
                        return (
                          <span key={i} style={{ display: 'inline-block', padding: '6px 14px', background: bg, color: '#fff', borderRadius: '4px', fontSize: '13px', fontWeight: 600 }}>
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              );
            })() : field.type === 'key_value' ? (() => {
              let keyOptions = null;
              if (fieldCatalog) {
                const resolvedSource = field.field_source_field
                  ? (formData[field.field_source_field] === 'task' ? 'task' : 'case')
                  : field.field_source;
                let sourceList = null;
                if (resolvedSource === 'task') {
                  sourceList = fieldCatalog.task_fields || [];
                } else if (resolvedSource === 'case') {
                  sourceList = fieldCatalog.case_fields || [];
                }
                if (sourceList) {
                  const caseTypeFilter = field.case_type_source_field ? String(formData[field.case_type_source_field] || '') : '';
                  const filtered = caseTypeFilter
                    ? sourceList.filter(f => f.is_default || (Array.isArray(f.case_type_ids) && f.case_type_ids.includes(caseTypeFilter)))
                    : sourceList;
                  keyOptions = filtered.map(f => ({ value: f.key, label: f.label || f.key }));
                }
              }
              return (
                <KeyValueEditor
                  value={formData[field.name]}
                  onChange={(val) => handleChange(field.name, val)}
                  keyLabel={field.key_label}
                  valueLabel={field.value_label}
                  keyOptions={keyOptions}
                  valueInput={field.value_input || 'text'}
                  expressionProps={field.value_input === 'expression' ? { ancestorNodes, nodeStatus, envVars } : null}
                />
              );
            })() : field.type === 'resource_picker' ? (
              <ResourcePicker
                value={formData[field.name] || ''}
                onChange={(val) => {
                  handleChange(field.name, val);
                  const split = field.split_fields;
                  if (split && split.separator && Array.isArray(split.targets)) {
                    const parts = String(val || '').split(split.separator);
                    split.targets.forEach((target, idx) => {
                      handleChange(target, parts[idx] || '');
                    });
                  }
                }}
                credentialId={formData[field.credential_field || 'credential_id']}
                resourceType={field.resource_type}
                parent={field.parent_field ? formData[field.parent_field] : undefined}
                placeholder={field.placeholder}
                style={inputStyle}
              />
            ) : field.type === 'model_cheat_sheet' ? (
              <ModelCheatSheet
                category={field.category || 'llm'}
                providerField={field.provider_field || 'provider'}
                modelField={field.model_field || 'model'}
                formData={formData}
              />
            ) : field.type === 'schema_browser' ? (
              <SchemaBrowser
                credentialId={formData[field.credential_field || 'credential_id'] || ''}
              />
            ) : field.type === 'xavvy_form_picker' ? (
              <XavvyFormPicker
                value={formData[field.name] || ''}
                onChange={(val) => handleChange(field.name, val)}
                workspaceId={currentWorkspaceId}
              />
            ) : field.type === 'webhook_url' ? (() => {
              const slug = (field.slug_field ? formData[field.slug_field] : formData.slug) || '';
              const webhookUrl = slug ? `${WEBHOOK_BASE_URL}${field.path_prefix || '/webhooks/'}${slug}` : '';
              const testWebhookUrl = slug ? `${WEBHOOK_BASE_URL}/webhooks/test/${slug}` : '';
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      readOnly
                      value={webhookUrl || '(enter a slug above)'}
                      style={{ ...inputStyle, flex: 1, color: webhookUrl ? 'var(--text-primary)' : 'var(--text-muted)', cursor: 'default', userSelect: 'all' }}
                      onFocus={(e) => e.target.select()}
                    />
                    {webhookUrl && (
                      <button
                        title="Copy URL"
                        onClick={() => { navigator.clipboard.writeText(webhookUrl); }}
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: 4, color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px 8px', fontSize: 13, flexShrink: 0 }}
                      >
                        Copy
                      </button>
                    )}
                  </div>
                  {testWebhookUrl && (
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 4 }}>
                        Test URL <span style={{ opacity: 0.7 }}>(runs draft · requires Authorization: Bearer &lt;token&gt;)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          readOnly
                          value={testWebhookUrl}
                          style={{ ...inputStyle, flex: 1, color: 'var(--warning-color)', cursor: 'default', userSelect: 'all', fontSize: 11 }}
                          onFocus={(e) => e.target.select()}
                        />
                        <button
                          title="Copy test URL"
                          onClick={() => { navigator.clipboard.writeText(testWebhookUrl); }}
                          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: 4, color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px 8px', fontSize: 13, flexShrink: 0 }}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })() : (
              <ExpressionInput
                value={formData[field.name] || field.default || ''}
                onChange={(val) => handleChange(field.name, val)}
                onDrop={(e) => handleInputDrop(e, formData[field.name], (val) => handleChange(field.name, val))}
                onDragOver={(e) => e.preventDefault()}
                placeholder={field.placeholder || ''}
                ancestorNodes={ancestorNodes}
                nodeStatus={nodeStatus}
                envVars={envVars}
                fieldLabel={field.label}
              />
            )}
            <VariablePreview value={formData[field.name]} context={previewContext} />
          </div>
        ))}
        {searchQuery && definition.fields.filter(isFieldVisible).filter(field => {
          if (resolvedDefinitionNodeType === 'if' && field.name === 'logic') return false;
          return matchesSearch(field);
        }).length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '12px', fontStyle: 'italic' }}>
            No fields match "{fieldSearch}".
          </div>
        )}
      </div>
    );
  };



  return (
    <div style={{...containerStyle, width: `${width}px`}} ref={panelRef}>
      <div
          onMouseDown={startResizing}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '5px',
            height: '100%',
            cursor: 'col-resize',
            zIndex: 100,
            backgroundColor: isResizing ? 'var(--accent-color)' : 'transparent',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--accent-color)'}
          onMouseLeave={(e) => !isResizing && (e.currentTarget.style.backgroundColor = 'transparent')}
        />
      {/* Sticky header: always-visible type badge + Run + Delete */}
      <div style={{
        position: 'sticky',
        top: 0,
        margin: '-20px -20px 12px -20px',
        padding: '10px 16px',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-color)',
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {label || 'Configure Node'}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
            {nodeType || selectedNode.type}
          </div>
        </div>
        <button
          onClick={handleRunNode}
          disabled={isRunning}
          title="Run this node"
          style={{
            padding: '6px 10px',
            background: 'var(--success-color)',
            color: 'var(--text-inverse)',
            border: 'none',
            borderRadius: '4px',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            opacity: isRunning ? 0.7 : 1,
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }}
        >
          {isRunning ? 'Running…' : '▶ Run'}
        </button>
        <button
          onClick={handleDelete}
          title="Delete this node"
          style={{
            padding: '6px 10px',
            background: 'transparent',
            color: 'var(--error-color)',
            border: '1px solid var(--error-color)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }}
        >
          Delete
        </button>
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={labelStyle}>Node Label</label>
        <input
          style={inputStyle}
          type="text"
          value={label}
          onChange={handleLabelChange}
          onDrop={(e) => handleInputDrop(e, label, (val) => {
              setLabel(val);
              setNodes((nds) => nds.map((node) => {
                  if (node.id === selectedNode.id) {
                      return { ...node, data: { ...node.data, label: val } };
                  }
                  return node;
              }));
          })}
          onDragOver={(e) => e.preventDefault()}
        />
      </div>
      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Node ID:</span>
        <code
          style={{ fontSize: '11px', color: 'var(--accent-color)', background: 'var(--bg-secondary)', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', userSelect: 'all' }}
          title="Click to copy node reference"
          onClick={() => copyToClipboard(`{{ nodes.${selectedNode.id}.data }}`)}
        >{selectedNode.id}</code>
        {copiedText === `{{ nodes.${selectedNode.id}.data }}` && (
          <span style={{ fontSize: '10px', color: 'var(--success-color)' }}>Copied!</span>
        )}
      </div>
      {/* Tab bar */}
      {selectedNode?.data?.type !== 'sticky_note' && selectedNode?.data?.type !== 'group' && (() => {
        const errorConfigured = !!formData._retry?.enabled
          || (parseInt(formData.retry_policy?.max_attempts) || 1) > 1
          || !!formData.rate_limit_policy?.enabled;
        const outputStatus = nodeStatus?.[selectedNode.id]?.status;
        const outputFailed = outputStatus === 'failed';
        const hasOutput = !!nodeStatus?.[selectedNode.id];
        const tabStyle = (active) => ({
          flex: 1,
          padding: '8px 10px',
          background: 'transparent',
          color: active ? 'var(--accent-color)' : 'var(--text-secondary)',
          border: 'none',
          borderBottom: active ? '2px solid var(--accent-color)' : '2px solid transparent',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: active ? 600 : 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          transition: 'color 0.15s, border-color 0.15s'
        });
        return (
          <div style={{ display: 'flex', marginBottom: '12px', borderBottom: '1px solid var(--border-color)' }}>
            <button onClick={() => setActiveTab('config')} style={tabStyle(activeTab === 'config')}>Config</button>
            <button onClick={() => setActiveTab('error')} style={tabStyle(activeTab === 'error')}>
              Error
              {errorConfigured && <span style={{ color: 'var(--accent-color)', fontSize: '14px', lineHeight: 1 }}>•</span>}
            </button>
            <button onClick={() => setActiveTab('output')} style={tabStyle(activeTab === 'output')}>
              Output
              {outputFailed && <span style={{ color: 'var(--error-color)', fontSize: '14px', lineHeight: 1 }}>•</span>}
              {hasOutput && !outputFailed && <span style={{ color: 'var(--success-color)', fontSize: '14px', lineHeight: 1 }}>•</span>}
            </button>
          </div>
        );
      })()}

      {/* Config tab */}
      {activeTab === 'config' && (<>
      {renderNodeHelperBuilder()}
      {nodeDefinitions[resolvedDefinitionNodeType] && renderDynamicFields(nodeDefinitions[resolvedDefinitionNodeType])}
      {nodeType === 'ai_agent' && (() => {
        const toolConnections = Array.isArray(formData.tool_connections) ? formData.tool_connections : [];
        if (toolConnections.length === 0) return null;
        const fallbackKey = getToolConnectionKey(toolConnections[0], 0);
        const activeKey = toolConnections.some((tc, idx) => getToolConnectionKey(tc, idx) === selectedToolConnectionKey)
          ? selectedToolConnectionKey
          : fallbackKey;
        const activeIndex = toolConnections.findIndex((tc, idx) => getToolConnectionKey(tc, idx) === activeKey);
        const activeTool = activeIndex >= 0 ? toolConnections[activeIndex] : toolConnections[0];
        const activeToolName = activeTool?.tool_name || activeTool?.node_type || `tool_${Math.max(1, activeIndex + 1)}`;
        const activeToolNodeId = activeTool?.node_id ? String(activeTool.node_id) : '';
        const activeDescription = activeTool?.description || '';
        return (
          <div style={{ marginTop: '16px', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px', background: 'var(--bg-tertiary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Connected Tools</div>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{toolConnections.length} total</div>
            </div>
            <label style={{ ...labelStyle, marginBottom: '4px' }}>Select tool</label>
            <select
              style={{ ...inputStyle, marginBottom: '10px' }}
              value={activeKey}
              onChange={(e) => setSelectedToolConnectionKey(e.target.value)}
            >
              {toolConnections.map((tc, idx) => {
                const optionKey = getToolConnectionKey(tc, idx);
                const toolName = tc?.tool_name || tc?.node_type || `tool_${idx + 1}`;
                const toolNodeId = tc?.node_id ? String(tc.node_id) : '';
                return (
                  <option key={optionKey} value={optionKey}>
                    {toolName}{toolNodeId ? ` (${toolNodeId})` : ''}
                  </option>
                );
              })}
            </select>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              ⚙ {activeToolName} {activeToolNodeId ? <span style={{ color: 'var(--text-tertiary)' }}>({activeToolNodeId})</span> : null}
            </div>
            <textarea
              style={{ ...textareaStyle, minHeight: '72px', marginBottom: 0 }}
              value={activeDescription}
              onChange={(e) => updateToolConnectionDescription(activeKey, e.target.value)}
              placeholder="Describe when and how the AI agent should use this tool"
            />
          </div>
        );
      })()}

      {/* Available Variables Section */}
      <div style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
        <h4 style={{ color: 'var(--text-primary)', marginTop: 0, marginBottom: '10px' }}>Available Variables</h4>
        <div style={{ maxHeight: '150px', overflowY: 'auto', paddingRight: '5px', background: 'var(--bg-tertiary)', borderRadius: '4px', padding: '10px' }}>
            <div style={{ marginBottom: '8px' }}>
                <div 
                    style={{ 
                        fontWeight: 'bold', 
                        fontSize: '12px', 
                        color: 'var(--accent-color)', 
                        cursor: 'grab', 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }} 
                    draggable
                    onDragStart={(e) => handleDragStart(e, `{{ nodes.prev_node.data }}`)}
                    onClick={() => copyToClipboard(`{{ nodes.prev_node.data }}`)}
                    title="Click to copy, drag to insert previous node reference"
                >
                    <span>prev_node</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', background: 'var(--bg-secondary)', padding: '2px 4px', borderRadius: '2px' }}>Copy Ref</span>
                </div>
                {/* Render nested keys for prev_node if available */}
                {prevNode && nodeStatus && nodeStatus[prevNode.id] && nodeStatus[prevNode.id].output && (
                    <div style={{ paddingLeft: '10px', marginTop: '2px', borderLeft: '1px solid var(--border-color)' }}>
                        {renderNestedKeys(nodeStatus[prevNode.id].output, `nodes.prev_node.data`)}
                    </div>
                )}
            </div>
            {ancestorNodes.length === 0 && (
                <div style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '12px' }}>No previous nodes available</div>
            )}
            
            {ancestorNodes.map(node => (
                <div key={node.id} style={{ marginBottom: '8px' }}>
                    <div 
                        style={{ 
                            fontWeight: 'bold', 
                            fontSize: '12px', 
                            color: 'var(--accent-color)', 
                            cursor: 'grab', 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }} 
                        draggable
                        onDragStart={(e) => handleDragStart(e, `{{ nodes['${node.data.label}'].data }}`)}
                        onClick={() => copyToClipboard(`{{ nodes['${node.data.label}'].data }}`)}
                        title="Click to copy, drag to insert node reference"
                    >
                        <span>{node.data.label}</span>
                        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', background: 'var(--bg-secondary)', padding: '2px 4px', borderRadius: '2px' }}>Copy Ref</span>
                    </div>
                    
                    {/* Special handling for Loop Node variables */}
                    {node.data.type === 'loop' && (
                        <div style={{ paddingLeft: '10px', marginTop: '2px', borderLeft: '1px solid var(--border-color)' }}>
                            <div 
                                style={{ fontSize: '11px', color: 'var(--text-secondary)', cursor: 'grab', padding: '2px 0', display: 'flex', alignItems: 'center' }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, `{{ nodes['${node.data.label}'].data.currentItem }}`)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(`{{ nodes['${node.data.label}'].data.currentItem }}`);
                                }}
                                title="Click to copy, drag to insert currentItem reference"
                            >
                                <span style={{ marginRight: '5px' }}>- currentItem</span>
                                <span style={{ color: 'var(--text-tertiary)' }}>: (Loop Item)</span>
                            </div>
                            <div 
                                style={{ fontSize: '11px', color: 'var(--text-secondary)', cursor: 'grab', padding: '2px 0', display: 'flex', alignItems: 'center' }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, `{{ nodes['${node.data.label}'].data.index }}`)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(`{{ nodes['${node.data.label}'].data.index }}`);
                                }}
                                title="Click to copy, drag to insert index reference"
                            >
                                <span style={{ marginRight: '5px' }}>- index</span>
                                <span style={{ color: 'var(--text-tertiary)' }}>: (Loop Index)</span>
                            </div>
                        </div>
                    )}
                    
                    {/* Show output keys if available */}
                    {nodeStatus && nodeStatus[node.id] && nodeStatus[node.id].output && typeof nodeStatus[node.id].output === 'object' && (
                        <div style={{ paddingLeft: '10px', marginTop: '2px', borderLeft: '1px solid var(--border-color)' }}>
                            {renderNestedKeys(nodeStatus[node.id].output, `nodes['${node.data.label}'].data`)}
                        </div>
                    )}
                </div>
            ))}
        </div>
        {copiedText && (
            <div style={{ 
                color: 'var(--accent-color)', 
                fontSize: '12px', 
                marginTop: '5px', 
                textAlign: 'center',
                background: 'var(--bg-tertiary)',
                padding: '4px',
                borderRadius: '4px',
                border: '1px solid var(--accent-color)'
            }}>
                Copied: {copiedText}
            </div>
        )}
      </div>
      </>)}

      {/* Error Handling tab */}
      {activeTab === 'error' && selectedNode?.data?.type !== 'sticky_note' && selectedNode?.data?.type !== 'group' && (() => {
        const simpleEnabled = !!formData._retry?.enabled;
        const advancedAttempts = parseInt(formData.retry_policy?.max_attempts) || 1;
        const rlEnabled = !!formData.rate_limit_policy?.enabled;
        return (
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '12px', fontStyle: 'italic', lineHeight: 1.4 }}>
              Configure how this node behaves when it fails. Simple Retry re-runs the whole node; Advanced Retry gives fine-grained control; Rate Limit handles 429 responses.
            </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                {/* Simple Retry — runner-level (_retry, §4.57) */}
                <div>
                  <div style={sectionHeaderStyle} onClick={() => setSimpleRetryOpen(v => !v)}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                      Simple Retry
                      {simpleEnabled && <span style={{ marginLeft: '6px', color: 'var(--accent-color)', fontWeight: 400 }}>· on</span>}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      {simpleRetryOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  {simpleRetryOpen && (
                    <div style={sectionBodyStyle}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={simpleEnabled}
                          onChange={e => handleChange('_retry', { ...(formData._retry || {}), enabled: e.target.checked })}
                          style={{ accentColor: 'var(--accent-color)', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Enable retry on any failure</span>
                      </label>
                      {simpleEnabled && (<>
                        <div>
                          <label style={labelStyle}>Max Attempts</label>
                          <input
                            type="number" min={2} max={10}
                            value={formData._retry?.max_attempts ?? 3}
                            onChange={e => handleChange('_retry', { ...formData._retry, max_attempts: Math.max(2, Math.min(10, parseInt(e.target.value, 10) || 3)) })}
                            style={{ ...inputStyle, marginBottom: 0 }}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Wait Strategy</label>
                          <select
                            value={formData._retry?.strategy ?? 'fixed'}
                            onChange={e => handleChange('_retry', { ...formData._retry, strategy: e.target.value })}
                            style={{ ...inputStyle, marginBottom: 0 }}
                          >
                            <option value="fixed">Fixed interval</option>
                            <option value="exponential">Exponential backoff</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Wait seconds (first retry)</label>
                          <input
                            type="number" min={1} max={300}
                            value={formData._retry?.wait_seconds ?? 5}
                            onChange={e => handleChange('_retry', { ...formData._retry, wait_seconds: Math.max(1, parseFloat(e.target.value) || 5) })}
                            style={{ ...inputStyle, marginBottom: 0 }}
                          />
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                          {formData._retry?.strategy === 'exponential'
                            ? `Waits ${formData._retry?.wait_seconds ?? 5}s, ${(formData._retry?.wait_seconds ?? 5) * 2}s, ${(formData._retry?.wait_seconds ?? 5) * 4}s… between attempts.`
                            : `Waits ${formData._retry?.wait_seconds ?? 5}s between each attempt.`}
                        </div>
                      </>)}
                    </div>
                  )}
                </div>

                {/* Advanced Retry — node-level (retry_policy) */}
                <div>
                  <div style={sectionHeaderStyle} onClick={() => setAdvancedRetryOpen(v => !v)}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                      Advanced Retry
                      {advancedAttempts > 1 && (
                        <span style={{ marginLeft: '6px', color: 'var(--accent-color)', fontWeight: 400 }}>
                          · {advancedAttempts} attempts
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      {advancedRetryOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  {advancedRetryOpen && (() => {
                    const rp = formData.retry_policy || {};
                    const updateRp = (patch) => handleUpdate({ ...formData, retry_policy: { ...rp, ...patch } });
                    const maxAttempts = parseInt(rp.max_attempts) || 1;
                    const backoffType = rp.backoff_type || 'fixed';
                    const retryOn = rp.retry_on || 'any';
                    return (
                      <div style={sectionBodyStyle}>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1.4, marginBottom: '-4px' }}>
                          Per-attempt control: backoff type, retry-on pattern, delay caps. Runs inside the node. Stacks with Simple Retry if both are set.
                        </div>
                        <div>
                          <label style={labelStyle}>Max Attempts</label>
                          <input
                            type="number" min="1" max="20"
                            style={{ ...inputStyle, marginBottom: 0 }}
                            value={maxAttempts}
                            onChange={(e) => updateRp({ max_attempts: Math.max(1, parseInt(e.target.value) || 1) })}
                          />
                          <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                            Total attempts including the first. 1 = no retry.
                          </p>
                        </div>
                        {maxAttempts > 1 && (<>
                          <div>
                            <label style={labelStyle}>Retry On</label>
                            <select
                              style={{ ...inputStyle, marginBottom: 0 }}
                              value={retryOn}
                              onChange={(e) => updateRp({ retry_on: e.target.value })}
                            >
                              <option value="any">Any error</option>
                              <option value="http_5xx">HTTP 5xx only</option>
                              <option value="timeout">Timeout only</option>
                              <option value="pattern">Error message matches pattern</option>
                            </select>
                          </div>
                          {retryOn === 'pattern' && (
                            <div>
                              <label style={labelStyle}>Error Pattern (regex)</label>
                              <ExpressionInput
                                value={rp.error_pattern || ''}
                                onChange={(val) => updateRp({ error_pattern: val })}
                                placeholder="e.g. rate.limit|quota exceeded"
                                ancestorNodes={ancestorNodes}
                                nodeStatus={nodeStatus}
                                envVars={envVars}
                              />
                              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                                Case-insensitive regex matched against the error message.
                              </p>
                            </div>
                          )}
                          <div>
                            <label style={labelStyle}>Backoff Type</label>
                            <select
                              style={{ ...inputStyle, marginBottom: 0 }}
                              value={backoffType}
                              onChange={(e) => updateRp({ backoff_type: e.target.value })}
                            >
                              <option value="fixed">Fixed delay</option>
                              <option value="exponential">Exponential backoff</option>
                            </select>
                          </div>
                          <div>
                            <label style={labelStyle}>Initial Delay (ms)</label>
                            <input
                              type="number" min="0" step="100"
                              style={{ ...inputStyle, marginBottom: 0 }}
                              value={parseInt(rp.initial_delay_ms ?? rp.backoff_ms ?? 0) || 0}
                              onChange={(e) => updateRp({ initial_delay_ms: Math.max(0, parseInt(e.target.value) || 0), backoff_ms: undefined })}
                            />
                          </div>
                          {backoffType === 'exponential' && (
                            <div>
                              <label style={labelStyle}>Multiplier</label>
                              <input
                                type="number" min="1.1" max="10" step="0.1"
                                style={{ ...inputStyle, marginBottom: 0 }}
                                value={parseFloat(rp.multiplier) || 2.0}
                                onChange={(e) => updateRp({ multiplier: Math.max(1.1, parseFloat(e.target.value) || 2.0) })}
                              />
                              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                                Each retry waits initial_delay × multiplier^(attempt−1).
                              </p>
                            </div>
                          )}
                          <div>
                            <label style={labelStyle}>Max Delay Cap (ms)</label>
                            <input
                              type="number" min="0" step="500"
                              style={{ ...inputStyle, marginBottom: 0 }}
                              value={parseInt(rp.max_delay_ms) || 0}
                              onChange={(e) => updateRp({ max_delay_ms: Math.max(0, parseInt(e.target.value) || 0) })}
                            />
                            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                              0 = no cap.
                            </p>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', padding: '8px 10px', borderRadius: '4px', lineHeight: 1.6 }}>
                            <strong>Summary:</strong> up to {maxAttempts} attempt{maxAttempts > 1 ? 's' : ''} · retry on <em>{retryOn === 'http_5xx' ? 'HTTP 5xx' : retryOn}</em> ·{' '}
                            {backoffType === 'exponential'
                              ? `exponential backoff starting at ${parseInt(rp.initial_delay_ms ?? rp.backoff_ms ?? 0) || 0} ms × ${parseFloat(rp.multiplier) || 2.0}`
                              : `fixed ${parseInt(rp.initial_delay_ms ?? rp.backoff_ms ?? 0) || 0} ms delay`}
                            {(parseInt(rp.max_delay_ms) || 0) > 0 ? `, capped at ${rp.max_delay_ms} ms` : ''}
                          </div>
                        </>)}
                      </div>
                    );
                  })()}
                </div>

                {/* Rate Limit Handling — rate_limit_policy */}
                <div>
                  <div style={sectionHeaderStyle} onClick={() => setRateLimitOpen(v => !v)}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                      Rate Limit Handling
                      {rlEnabled && (
                        <span style={{ marginLeft: '6px', color: 'var(--accent-color)', fontWeight: 400 }}>
                          · {formData.rate_limit_policy?.base_delay_s ?? 60}s base
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      {rateLimitOpen ? '▼' : '▶'}
                    </span>
                  </div>
                  {rateLimitOpen && (() => {
                    const rl = formData.rate_limit_policy || {};
                    const updateRl = (patch) => handleUpdate({ ...formData, rate_limit_policy: { ...rl, ...patch } });
                    return (
                      <div style={sectionBodyStyle}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={rlEnabled}
                            onChange={(e) => updateRl({ enabled: e.target.checked })}
                            style={{ accentColor: 'var(--accent-color)', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Auto-retry on 429 / Too Many Requests</span>
                        </label>
                        {rlEnabled && (<>
                          <div>
                            <label style={labelStyle}>Max Rate-Limit Retries</label>
                            <input
                              type="number" min="1" max="20"
                              style={{ ...inputStyle, marginBottom: 0 }}
                              value={parseInt(rl.max_retries) || 3}
                              onChange={(e) => updateRl({ max_retries: Math.max(1, parseInt(e.target.value) || 1) })}
                            />
                            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                              Does not count against retry attempts above.
                            </p>
                          </div>
                          <div>
                            <label style={labelStyle}>Base Delay (seconds)</label>
                            <input
                              type="number" min="1" max="300" step="5"
                              style={{ ...inputStyle, marginBottom: 0 }}
                              value={parseInt(rl.base_delay_s) || 60}
                              onChange={(e) => updateRl({ base_delay_s: Math.max(1, parseInt(e.target.value) || 60) })}
                            />
                            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                              Used when the API does not supply a <code>Retry-After</code> header.
                            </p>
                          </div>
                          <div>
                            <label style={labelStyle}>Max Wait Cap (seconds)</label>
                            <input
                              type="number" min="0" max="3600" step="30"
                              style={{ ...inputStyle, marginBottom: 0 }}
                              value={parseInt(rl.max_wait_s) ?? 300}
                              onChange={(e) => updateRl({ max_wait_s: Math.max(0, parseInt(e.target.value) || 0) })}
                            />
                            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
                              0 = no cap.
                            </p>
                          </div>
                        </>)}
                      </div>
                    );
                  })()}
                </div>

              </div>
          </div>
        );
      })()}

      {/* Output tab */}
      {activeTab === 'output' && (<>
      {/* Node Execution Output */}
      {nodeStatus && nodeStatus[selectedNode.id] && (
        <div style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
             <h4 style={{ color: 'var(--text-primary)', marginTop: 0 }}>Execution Output</h4>
             <div style={{
                 margin: '5px 0',
                 color: nodeStatus[selectedNode.id].status === 'completed' ? 'var(--success-color)' :
                        nodeStatus[selectedNode.id].status === 'failed' ? 'var(--error-color)' : 'var(--warning-color)',
                 fontSize: '12px'
             }}>
                 Status: {nodeStatus[selectedNode.id].status}
             </div>
             <pre
                 tabIndex={0}
                 onKeyDown={(e) => {
                     if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
                         e.preventDefault();
                         e.stopPropagation();
                         const range = document.createRange();
                         range.selectNodeContents(e.currentTarget);
                         const sel = window.getSelection();
                         sel.removeAllRanges();
                         sel.addRange(range);
                     }
                 }}
                 style={{ background: 'var(--bg-tertiary)', padding: '10px', borderRadius: '4px', fontSize: '12px', whiteSpace: 'pre-wrap', color: 'var(--text-primary)', border: '1px solid var(--border-color)', maxHeight: '200px', overflow: 'auto', outline: 'none' }}
             >
                 {JSON.stringify(nodeStatus[selectedNode.id].output, null, 2)}
             </pre>
        </div>
      )}

      {/* Output Field Hints (from node catalog — shown when no execution output yet) */}
      {(() => {
        const catalogEntry = nodeCatalog.find(n => n.type === selectedNode?.data?.type);
        const hints = catalogEntry?.output_fields;
        if (!hints || !hints.length) return null;
        if (nodeStatus && nodeStatus[selectedNode.id]?.output) return null; // already showing real output above
        const nodeLabel = selectedNode?.data?.label || selectedNode?.data?.type || 'Node';
        return (
          <div style={{ marginTop: '20px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
            <h4 style={{ color: 'var(--text-primary)', marginTop: 0, fontSize: '13px' }}>Output Fields</h4>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              Available after execution. Click to copy expression.
            </div>
            <div style={{ background: 'var(--bg-tertiary)', borderRadius: '4px', border: '1px solid var(--border-color)', padding: '6px 0' }}>
              {hints.map(field => {
                const expr = `{{ nodes['${nodeLabel}'].data.${field} }}`;
                return (
                  <div
                    key={field}
                    onClick={() => { navigator.clipboard.writeText(expr); setCopiedText(expr); setTimeout(() => setCopiedText(null), 1500); }}
                    style={{ padding: '3px 10px', cursor: 'pointer', fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span>{field}</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>click to copy</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Empty-state for Output tab when no data yet */}
      {(() => {
        const catalogEntry = nodeCatalog.find(n => n.type === selectedNode?.data?.type);
        const hasHints = catalogEntry?.output_fields?.length > 0;
        const hasOutput = !!nodeStatus?.[selectedNode.id];
        if (hasOutput || hasHints) return null;
        return (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '12px', fontStyle: 'italic' }}>
            No execution output yet. Run this node to see results.
          </div>
        );
      })()}
      </>)}
    </div>
  );
}
